import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, CardHeader, CardBody, Button, Table, Input, Badge, Row, Col, Label,
    Progress, Spinner
} from 'reactstrap';
import Toastify from 'toastify-js';
import 'toastify-js/src/toastify.css';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import DownloadFileService from 'services/fileManager.service';
import settingService from 'services/setting.service';
import 'assets/css/dhb-render-model.css';

// ═══════ AI Models Categories ═══════
const AI_CATEGORIES = [
    {
        title: 'Translate',
        icon: 'fas fa-language',
        color: '#3498db',
        keys: [
            { key: 'aiModels_openai_translate', label: 'OpenAI' },
            { key: 'aiModels_grok_translate', label: 'Grok' },
            { key: 'aiModels_perplexity_translate', label: 'Perplexity' },
            { key: 'aiModels_yescale_translate', label: 'YEScale' },
            { key: 'aiModels_deepseek_translate', label: 'DeepSeek' },
        ]
    },
    {
        title: 'Content Generation',
        icon: 'fas fa-pen-fancy',
        color: '#9b59b6',
        keys: [
            { key: 'aiModels_openai_prompt', label: 'OpenAI' },
            { key: 'aiModels_gemini_prompt', label: 'Google Gemini' },
            { key: 'aiModels_grok_prompt', label: 'Grok' },
            { key: 'aiModels_perplexity_prompt', label: 'Perplexity' },
            { key: 'aiModels_deepseek_prompt', label: 'DeepSeek' },
        ]
    },
    {
        title: 'Speech & Image',
        icon: 'fas fa-microphone-alt',
        color: '#e67e22',
        keys: [
            { key: 'aiModels_openai_speech', label: 'OpenAI Speech/TTS' },
            { key: 'aiModels_openai_image', label: 'OpenAI Image' },
            { key: 'aiModels_openai_whisper', label: 'OpenAI Whisper' },
        ]
    },
    {
        title: 'Ollama (Local AI)',
        icon: 'fas fa-server',
        color: '#1abc9c',
        keys: [
            { key: 'aiModels_ollama_translate', label: 'Ollama Translate', hasSize: true },
        ]
    },
    {
        title: 'Speech (Coqui XTTS)',
        icon: 'fas fa-comment-dots',
        color: '#e74c3c',
        keys: [
            { key: 'aiModels_coqui_xtts', label: 'Coqui XTTS Repos', isRepo: true },
        ]
    }
];

function DHBRenderModel() {
    const [manifest, setManifest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Piper state
    const [piperManifest, setPiperManifest] = useState(null);
    const [piperLoading, setPiperLoading] = useState(true);
    const [piperSaving, setPiperSaving] = useState(false);

    // CoquiTTS state
    const [coquiManifest, setCoquiManifest] = useState(null);
    const [coquiLoading, setCoquiLoading] = useState(true);
    const [coquiSaving, setCoquiSaving] = useState(false);

    // OmniVoice state
    const [omniVoiceManifest, setOmniVoiceManifest] = useState(null);
    const [omniVoiceLoading, setOmniVoiceLoading] = useState(true);
    const [omniVoiceSaving, setOmniVoiceSaving] = useState(false);

    // VocalRemover state
    const [vocalRemoverManifest, setVocalRemoverManifest] = useState(null);
    const [vocalRemoverLoading, setVocalRemoverLoading] = useState(true);
    const [vocalRemoverSaving, setVocalRemoverSaving] = useState(false);

    // File browser
    const [versionFiles, setVersionFiles] = useState([]);
    const [modelFiles, setModelFiles] = useState([]);
    const [piperEngineFiles, setPiperEngineFiles] = useState([]);
    const [piperModelFiles, setPiperModelFiles] = useState([]);
    const [coquiEngineFiles, setCoquiEngineFiles] = useState([]);
    const [coquiModelFiles, setCoquiModelFiles] = useState([]);
    const [omniVoiceEngineFiles, setOmniVoiceEngineFiles] = useState([]);
    const [vocalRemoverEngineFiles, setVocalRemoverEngineFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});

    // AI Models state
    const [aiSettings, setAiSettings] = useState({});
    const [aiSaving, setAiSaving] = useState({});
    const [editingKey, setEditingKey] = useState(null);
    const [editModels, setEditModels] = useState({});
    const [aiLoading, setAiLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('subtitle');

    const formatSize = (size) => {
        if (!size) return '—';
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    const showToast = (text, bg) => {
        Toastify({ text, backgroundColor: bg, close: true, position: 'right', gravity: 'bottom' }).showToast();
    };

    // ═══════ Manifest ═══════
    const loadManifest = useCallback(async () => {
        setLoading(true);
        try {
            const res = await DownloadFileService.getWhisperManifest();
            setManifest(res.data);
        } catch {
            setManifest({ latestVersion: '', models: [], versions: [] });
        } finally {
            setLoading(false);
        }
    }, []);

    const saveManifest = async (updatedManifest) => {
        setSaving(true);
        try {
            await DownloadFileService.updateWhisperManifest(updatedManifest);
            setManifest(updatedManifest);
            showToast('Lưu manifest thành công', '#4caf50');
        } catch (err) {
            showToast(`Lỗi lưu manifest: ${err}`, '#f44336');
        } finally {
            setSaving(false);
        }
    };

    // ═══════ File loading ═══════
    const loadEngineFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/Whisper');
            setVersionFiles(res.data || []);
        } catch {
            setVersionFiles([]);
        }
    }, []);

    const loadModelFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/Whisper/Models');
            setModelFiles(res.data || []);
        } catch {
            setModelFiles([]);
        }
    }, []);

    // ═══════ Piper Manifest ═══════
    const loadPiperManifest = useCallback(async () => {
        setPiperLoading(true);
        try {
            const res = await DownloadFileService.getPiperManifest();
            setPiperManifest(res.data);
        } catch {
            setPiperManifest({ latestVersion: '', models: [], versions: [] });
        } finally {
            setPiperLoading(false);
        }
    }, []);

    const savePiperManifest = async (updatedManifest) => {
        setPiperSaving(true);
        try {
            await DownloadFileService.updatePiperManifest(updatedManifest);
            setPiperManifest(updatedManifest);
            showToast('Lưu Piper manifest thành công', '#4caf50');
        } catch (err) {
            showToast(`Lỗi lưu Piper manifest: ${err}`, '#f44336');
        } finally {
            setPiperSaving(false);
        }
    };

    // ═══════ CoquiTTS Manifest ═══════
    const loadCoquiManifest = useCallback(async () => {
        setCoquiLoading(true);
        try {
            const res = await DownloadFileService.getCoquiManifest();
            setCoquiManifest(res.data);
        } catch {
            setCoquiManifest({ latestVersion: '', versions: [], models: [] });
        } finally {
            setCoquiLoading(false);
        }
    }, []);

    const saveCoquiManifest = async (updatedManifest) => {
        setCoquiSaving(true);
        try {
            await DownloadFileService.updateCoquiManifest(updatedManifest);
            setCoquiManifest(updatedManifest);
            showToast('Lưu CoquiTTS manifest thành công', '#4caf50');
        } catch (err) {
            showToast(`Lỗi lưu CoquiTTS manifest: ${err}`, '#f44336');
        } finally {
            setCoquiSaving(false);
        }
    };

    // ═══════ OmniVoice Manifest ═══════
    const loadOmniVoiceManifest = useCallback(async () => {
        setOmniVoiceLoading(true);
        try {
            const res = await DownloadFileService.getOmniVoiceManifest();
            setOmniVoiceManifest(res.data);
        } catch {
            setOmniVoiceManifest({ latestVersion: '', versions: [] });
        } finally {
            setOmniVoiceLoading(false);
        }
    }, []);

    const saveOmniVoiceManifest = async (updatedManifest) => {
        setOmniVoiceSaving(true);
        try {
            await DownloadFileService.updateOmniVoiceManifest(updatedManifest);
            setOmniVoiceManifest(updatedManifest);
            showToast('Lưu OmniVoice manifest thành công', '#4caf50');
        } catch (err) {
            showToast(`Lỗi lưu OmniVoice manifest: ${err}`, '#f44336');
        } finally {
            setOmniVoiceSaving(false);
        }
    };

    // ═══════ VocalRemover Manifest ═══════
    const loadVocalRemoverManifest = useCallback(async () => {
        setVocalRemoverLoading(true);
        try {
            const res = await DownloadFileService.getVocalRemoverManifest();
            setVocalRemoverManifest(res.data);
        } catch {
            setVocalRemoverManifest({ latestVersion: '', versions: [] });
        } finally {
            setVocalRemoverLoading(false);
        }
    }, []);

    const saveVocalRemoverManifest = async (updatedManifest) => {
        setVocalRemoverSaving(true);
        try {
            await DownloadFileService.updateVocalRemoverManifest(updatedManifest);
            setVocalRemoverManifest(updatedManifest);
            showToast('Lưu VocalRemover manifest thành công', '#4caf50');
        } catch (err) {
            showToast(`Lỗi lưu VocalRemover manifest: ${err}`, '#f44336');
        } finally {
            setVocalRemoverSaving(false);
        }
    };

    // ═══════ Piper File loading ═══════
    const loadPiperEngineFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/PiperTTS');
            setPiperEngineFiles(res.data || []);
        } catch {
            setPiperEngineFiles([]);
        }
    }, []);

    const loadPiperModelFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/PiperTTS/Models');
            setPiperModelFiles(res.data || []);
        } catch {
            setPiperModelFiles([]);
        }
    }, []);

    // ═══════ CoquiTTS File loading ═══════
    const loadCoquiEngineFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/CoquiTTS');
            setCoquiEngineFiles(res.data || []);
        } catch {
            setCoquiEngineFiles([]);
        }
    }, []);

    const loadCoquiModelFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/CoquiTTS/Models');
            setCoquiModelFiles(res.data || []);
        } catch {
            setCoquiModelFiles([]);
        }
    }, []);

    // ═══════ OmniVoice File loading ═══════
    const loadOmniVoiceEngineFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/OmniVoice');
            setOmniVoiceEngineFiles(res.data || []);
        } catch {
            setOmniVoiceEngineFiles([]);
        }
    }, []);

    // ═══════ VocalRemover File loading ═══════
    const loadVocalRemoverEngineFiles = useCallback(async () => {
        try {
            const res = await DownloadFileService.getFiles('Plugin/VocalRemover');
            setVocalRemoverEngineFiles(res.data || []);
        } catch {
            setVocalRemoverEngineFiles([]);
        }
    }, []);

    useEffect(() => { loadManifest(); loadPiperManifest(); loadCoquiManifest(); loadOmniVoiceManifest(); loadVocalRemoverManifest(); loadAiSettings(); }, [loadManifest, loadPiperManifest, loadCoquiManifest, loadOmniVoiceManifest, loadVocalRemoverManifest]);

    useEffect(() => {
        loadEngineFiles();
        loadModelFiles();
    }, [manifest, loadEngineFiles, loadModelFiles]);

    useEffect(() => {
        loadPiperEngineFiles();
        loadPiperModelFiles();
    }, [piperManifest, loadPiperEngineFiles, loadPiperModelFiles]);

    useEffect(() => {
        loadCoquiEngineFiles();
        loadCoquiModelFiles();
    }, [coquiManifest, loadCoquiEngineFiles, loadCoquiModelFiles]);

    useEffect(() => {
        loadOmniVoiceEngineFiles();
    }, [omniVoiceManifest, loadOmniVoiceEngineFiles]);

    useEffect(() => {
        loadVocalRemoverEngineFiles();
    }, [vocalRemoverManifest, loadVocalRemoverEngineFiles]);

    // ═══════ Version Actions ═══════
    const handleSetLatest = (version) => {
        saveManifest({ ...manifest, latestVersion: version });
    };

    const showAddVersionDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm phiên bản Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Engine Type</label>
                    <select id="swal-engine-type" class="swal2-select" style="margin:0 0 12px 0;width:100%;padding:6px 10px;border:1px solid #d1d5db;border-radius:6px">
                        <option value="stable-ts">Whisper Stable TS</option>
                        <option value="xxl">Whisper XXL</option>
                    </select>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version (vd: v2)</label>
                    <input id="swal-version" class="swal2-input" placeholder="v2" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Engine (.zip)</label>
                    <input id="swal-engine-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-file-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-date" type="date" class="swal2-input" value="${new Date().toISOString().split('T')[0]}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-changelog" class="swal2-textarea" placeholder="Mô tả thay đổi..." style="margin:0;width:100%"></textarea>
                    <div id="swal-progress" style="margin-top:10px;display:none">
                        <small id="swal-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-progress-bar" style="background:#6366f1;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-engine-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-file-info').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                const engineId = document.getElementById('swal-engine-type').value;
                const version = document.getElementById('swal-version').value.trim();
                const releaseDate = document.getElementById('swal-date').value;
                const changelog = document.getElementById('swal-changelog').value.trim();

                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file engine');

                // Show progress
                const progressDiv = document.getElementById('swal-progress');
                const progressBar = document.getElementById('swal-progress-bar');
                const progressText = document.getElementById('swal-progress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/Whisper', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return { version, engineId, engineFile: selectedFile.name, releaseDate, changelog };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const v = result.value;
                // Xóa version cũ nếu trùng cùng engine, rồi thêm version mới lên đầu
                const filteredVersions = (manifest.versions || []).filter(ver => !(ver.version === v.version && ver.engineId === v.engineId));
                const latestKey = v.engineId === 'stable-ts' ? 'latestVersionStableTS' : 'latestVersionXXL';
                const updated = {
                    ...manifest,
                    [latestKey]: v.version,
                    versions: [{ version: v.version, engineId: v.engineId, engineFile: v.engineFile, releaseDate: v.releaseDate, changelog: v.changelog }, ...filteredVersions]
                };
                saveManifest(updated);
                loadEngineFiles();
            }
        });
    };

    const handleAddVersion = showAddVersionDialog;

    const handleDeleteVersion = (idx) => {
        Swal.fire({
            title: 'Xóa phiên bản?',
            text: `Xóa "${manifest.versions[idx].version}" khỏi manifest? (Không xóa file trên server)`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                saveManifest({ ...manifest, versions: manifest.versions.filter((_, i) => i !== idx) });
            }
        });
    };

    const showAddModelDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm Model Weight',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên hiển thị (vd: Medium)</label>
                    <input id="swal-model-name" class="swal2-input" placeholder="Medium" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Model (.zip)</label>
                    <input id="swal-model-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-mfile-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <div id="swal-mprogress" style="margin-top:10px;display:none">
                        <small id="swal-mprogress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-mprogress-bar" style="background:#f59e0b;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-model-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-mfile-info').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                const name = document.getElementById('swal-model-name').value.trim();

                if (!name) return Swal.showValidationMessage('Vui lòng nhập tên model');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file model');

                const progressDiv = document.getElementById('swal-mprogress');
                const progressBar = document.getElementById('swal-mprogress-bar');
                const progressText = document.getElementById('swal-mprogress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/Whisper/Models', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return { name, file: selectedFile.name, size: selectedFile.size };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const m = result.value;
                const updated = {
                    ...manifest,
                    models: [...(manifest.models || []), { name: m.name, file: m.file, size: m.size }]
                };
                saveManifest(updated);
                loadModelFiles();
            }
        });
    };

    const handleAddSharedModel = showAddModelDialog;

    const handleDeleteSharedModel = (mIdx) => {
        const model = manifest.models[mIdx];
        Swal.fire({
            title: `Xóa model "${model.name}"?`,
            text: 'Chỉ xóa khỏi manifest, không xóa file.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                saveManifest({ ...manifest, models: manifest.models.filter((_, i) => i !== mIdx) });
            }
        });
    };

    // ═══════ Piper Version Actions ═══════
    const handleSetPiperLatest = (version) => {
        savePiperManifest({ ...piperManifest, latestVersion: version });
    };

    const showAddPiperVersionDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm phiên bản Piper Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version (vd: v1)</label>
                    <input id="swal-piper-version" class="swal2-input" placeholder="v1" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Engine (.zip)</label>
                    <input id="swal-piper-engine-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-piper-file-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path (phía client)</label>
                    <input id="swal-piper-extract-path" class="swal2-input" placeholder="lib/PiperTTS" value="lib/PiperTTS" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-piper-date" type="date" class="swal2-input" value="${new Date().toISOString().split('T')[0]}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-piper-changelog" class="swal2-textarea" placeholder="Mô tả thay đổi..." style="margin:0;width:100%"></textarea>
                    <div id="swal-piper-progress" style="margin-top:10px;display:none">
                        <small id="swal-piper-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-piper-progress-bar" style="background:#10b981;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-piper-engine-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-piper-file-info').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                const version = document.getElementById('swal-piper-version').value.trim();
                const releaseDate = document.getElementById('swal-piper-date').value;
                const changelog = document.getElementById('swal-piper-changelog').value.trim();

                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file engine');

                const progressDiv = document.getElementById('swal-piper-progress');
                const progressBar = document.getElementById('swal-piper-progress-bar');
                const progressText = document.getElementById('swal-piper-progress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/PiperTTS', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    const engineExtractPath = document.getElementById('swal-piper-extract-path').value.trim() || 'lib/PiperTTS';
                    return { version, engineFile: selectedFile.name, engineExtractPath, releaseDate, changelog };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const v = result.value;
                const filteredVersions = (piperManifest.versions || []).filter(ver => ver.version !== v.version);
                const updated = {
                    ...piperManifest,
                    latestVersion: v.version,
                    versions: [{ version: v.version, engineFile: v.engineFile, engineExtractPath: v.engineExtractPath, releaseDate: v.releaseDate, changelog: v.changelog, plugins: [] }, ...filteredVersions]
                };
                savePiperManifest(updated);
                loadPiperEngineFiles();
            }
        });
    };

    const handleDeletePiperVersion = (idx) => {
        Swal.fire({
            title: 'Xóa phiên bản?',
            text: `Xóa "${piperManifest.versions[idx].version}" khỏi manifest? (Không xóa file trên server)`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                savePiperManifest({ ...piperManifest, versions: piperManifest.versions.filter((_, i) => i !== idx) });
            }
        });
    };

    // ═══════ Piper Plugin Actions ═══════
    const showAddPluginToVersionDialog = (versionIdx) => {
        let selectedFile = null;
        const ver = (piperManifest.versions || [])[versionIdx];
        if (!ver) return;
        Swal.fire({
            title: `Thêm Plugin cho ${ver.version}`,
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Plugin ID (vd: cuda, tensorrt)</label>
                    <input id="swal-plugin-id" class="swal2-input" placeholder="cuda" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên hiển thị</label>
                    <input id="swal-plugin-name" class="swal2-input" placeholder="CUDA Acceleration" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Plugin (.zip)</label>
                    <input id="swal-plugin-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-plugin-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path (phía client)</label>
                    <input id="swal-plugin-extract-path" class="swal2-input" placeholder="lib/PiperTTS" value="lib/PiperTTS" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Mô tả</label>
                    <input id="swal-plugin-desc" class="swal2-input" placeholder="Hỗ trợ GPU NVIDIA qua CUDA" style="margin:0 0 12px 0;width:100%">
                    <div style="margin-bottom:12px">
                        <label style="font-size:12px;display:flex;align-items:center;gap:6px;cursor:pointer">
                            <input type="checkbox" id="swal-plugin-required"> Bắt buộc (required)
                        </label>
                    </div>
                    <div id="swal-plugin-progress" style="margin-top:10px;display:none">
                        <small id="swal-plugin-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-plugin-progress-bar" style="background:#6366f1;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-plugin-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-plugin-finfo').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                const id = document.getElementById('swal-plugin-id').value.trim();
                const name = document.getElementById('swal-plugin-name').value.trim();
                if (!id) return Swal.showValidationMessage('Vui lòng nhập Plugin ID');
                if (!name) return Swal.showValidationMessage('Vui lòng nhập tên');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file plugin');

                const progressDiv = document.getElementById('swal-plugin-progress');
                const progressBar = document.getElementById('swal-plugin-progress-bar');
                const progressText = document.getElementById('swal-plugin-progress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/PiperTTS', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return {
                        id,
                        name,
                        file: selectedFile.name,
                        extractPath: document.getElementById('swal-plugin-extract-path').value.trim() || 'lib/PiperTTS',
                        description: document.getElementById('swal-plugin-desc').value.trim(),
                        required: document.getElementById('swal-plugin-required').checked
                    };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const versions = [...(piperManifest.versions || [])];
                const plugins = [...(versions[versionIdx].plugins || [])];
                // Replace if same id exists, otherwise append
                const existingIdx = plugins.findIndex(p => p.id === result.value.id);
                if (existingIdx >= 0) {
                    plugins[existingIdx] = result.value;
                } else {
                    plugins.push(result.value);
                }
                versions[versionIdx] = { ...versions[versionIdx], plugins };
                savePiperManifest({ ...piperManifest, versions });
                loadPiperEngineFiles();
            }
        });
    };

    const handleDeletePluginFromVersion = (versionIdx, pluginIdx) => {
        const ver = (piperManifest.versions || [])[versionIdx];
        const plugin = (ver?.plugins || [])[pluginIdx];
        if (!plugin) return;
        Swal.fire({
            title: `Xóa plugin "${plugin.name}"?`,
            text: 'Chỉ xóa khỏi manifest, không xóa file trên server.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                const versions = [...(piperManifest.versions || [])];
                versions[versionIdx] = {
                    ...versions[versionIdx],
                    plugins: (versions[versionIdx].plugins || []).filter((_, i) => i !== pluginIdx)
                };
                savePiperManifest({ ...piperManifest, versions });
            }
        });
    };

    // ═══════ Piper CUDA Upload ═══════
    const showUploadCudaDialog = (versionIdx) => {
        let selectedFile = null;
        const ver = (piperManifest.versions || [])[versionIdx];
        if (!ver) return;
        Swal.fire({
            title: `Tải lên CUDA cho ${ver.version}`,
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File CUDA (.zip)</label>
                    <input id="swal-cuda-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-cuda-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path (phía client)</label>
                    <input id="swal-cuda-extract" class="swal2-input" value="lib/PiperTTS" style="margin:0 0 12px 0;width:100%">
                    <div id="swal-cuda-progress" style="margin-top:10px;display:none">
                        <small id="swal-cuda-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-cuda-progress-bar" style="background:#10b981;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-cuda-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-cuda-finfo').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file CUDA');

                const progressDiv = document.getElementById('swal-cuda-progress');
                const progressBar = document.getElementById('swal-cuda-progress-bar');
                const progressText = document.getElementById('swal-cuda-progress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/PiperTTS', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return {
                        file: selectedFile.name,
                        extractPath: document.getElementById('swal-cuda-extract').value.trim() || 'lib/PiperTTS'
                    };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const versions = [...(piperManifest.versions || [])];
                const plugins = [...(versions[versionIdx].plugins || [])];
                const cudaIdx = plugins.findIndex(p => p.id === 'cuda');
                const cudaPlugin = {
                    id: 'cuda',
                    name: 'CUDA Acceleration',
                    file: result.value.file,
                    extractPath: result.value.extractPath,
                    description: 'Hỗ trợ GPU NVIDIA qua CUDA',
                    required: false,
                    updatedAt: new Date().toISOString().split('T')[0]
                };
                if (cudaIdx >= 0) {
                    plugins[cudaIdx] = cudaPlugin;
                } else {
                    plugins.push(cudaPlugin);
                }
                versions[versionIdx] = { ...versions[versionIdx], plugins };
                savePiperManifest({ ...piperManifest, versions });
                loadPiperEngineFiles();
            }
        });
    };

    const handleRemoveCuda = (versionIdx) => {
        const ver = (piperManifest.versions || [])[versionIdx];
        const cudaPlugin = (ver?.plugins || []).find(p => p.id === 'cuda');
        if (!cudaPlugin) return;
        Swal.fire({
            title: 'Gỡ CUDA plugin?',
            text: `Gỡ "${cudaPlugin.file}" khỏi manifest (không xóa file trên server).`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Gỡ',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                const versions = [...(piperManifest.versions || [])];
                versions[versionIdx] = {
                    ...versions[versionIdx],
                    plugins: (versions[versionIdx].plugins || []).filter(p => p.id !== 'cuda')
                };
                savePiperManifest({ ...piperManifest, versions });
            }
        });
    };

    // ═══════ Piper Catalog Actions ═══════
    const showAddPiperCatalogGroupDialog = () => {
        let selectedModelFile = null;
        Swal.fire({
            title: 'Thêm nguồn Model',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">ID (vd: nghitts, bentts)</label>
                    <input id="swal-cat-id" class="swal2-input" placeholder="nghitts" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên hiển thị</label>
                    <input id="swal-cat-name" class="swal2-input" placeholder="NghiTTS Vietnamese" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Mô tả</label>
                    <input id="swal-cat-desc" class="swal2-input" placeholder="Bộ giọng nói tiếng Việt" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tác giả</label>
                    <input id="swal-cat-author" class="swal2-input" placeholder="NghiTTS" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">License</label>
                    <input id="swal-cat-license" class="swal2-input" placeholder="Apache-2.0" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Ngôn ngữ chính (vi / en / multi)</label>
                    <input id="swal-cat-lang" class="swal2-input" placeholder="vi" style="margin:0 0 12px 0;width:100%">
                    <hr style="border-color:#e2e8f0;margin:16px 0 12px">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">
                        <i class="fas fa-brain" style="color:#8b5cf6;margin-right:4px"></i>Model Pack (tuỳ chọn)
                    </label>
                    <input id="swal-cat-model-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-cat-model-info" style="font-size:11px;color:#64748b;margin-bottom:8px">Chọn file model pack nếu nguồn có preprocessing</div>
                    <div style="margin-bottom:12px">
                        <label style="font-size:12px;display:flex;align-items:center;gap:6px;cursor:pointer">
                            <input type="checkbox" id="swal-cat-has-preprocess" checked> Có text preprocessing
                        </label>
                    </div>
                    <div id="swal-cat-model-progress" style="margin-top:4px;display:none">
                        <small id="swal-cat-model-progress-text">Đang tải lên Model Pack...</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:6px;margin-top:4px;overflow:hidden">
                            <div id="swal-cat-model-progress-bar" style="background:#8b5cf6;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-cat-model-file').addEventListener('change', (e) => {
                    selectedModelFile = e.target.files[0];
                    if (selectedModelFile) {
                        document.getElementById('swal-cat-model-info').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedModelFile.name} (${(selectedModelFile.size / 1024).toFixed(1)} KB)`;
                    }
                });
            },
            preConfirm: async () => {
                const id = document.getElementById('swal-cat-id').value.trim();
                const name = document.getElementById('swal-cat-name').value.trim();
                if (!id) return Swal.showValidationMessage('Vui lòng nhập ID');
                if (!name) return Swal.showValidationMessage('Vui lòng nhập tên');

                let modelFile = null;
                let modelSize = 0;

                // Upload model pack if selected
                if (selectedModelFile) {
                    const progressDiv = document.getElementById('swal-cat-model-progress');
                    const progressBar = document.getElementById('swal-cat-model-progress-bar');
                    const progressText = document.getElementById('swal-cat-model-progress-text');
                    progressDiv.style.display = 'block';
                    Swal.showLoading();

                    try {
                        await DownloadFileService.uploadFileChunked('Plugin/PiperTTS/Models', selectedModelFile, (e) => {
                            const pct = Math.round((e.loaded / e.total) * 100);
                            progressBar.style.width = pct + '%';
                            progressText.textContent = `Đang tải lên Model Pack... ${pct}%`;
                        });
                        modelFile = selectedModelFile.name;
                        modelSize = selectedModelFile.size;
                    } catch (err) {
                        Swal.showValidationMessage(`Lỗi tải lên Model Pack: ${err.message || err}`);
                        return;
                    }
                }

                return {
                    id,
                    name,
                    description: document.getElementById('swal-cat-desc').value.trim(),
                    author: document.getElementById('swal-cat-author').value.trim(),
                    license: document.getElementById('swal-cat-license').value.trim(),
                    language: document.getElementById('swal-cat-lang').value.trim() || 'vi',
                    modelFile,
                    modelSize,
                    hasPreprocessing: document.getElementById('swal-cat-has-preprocess').checked,
                    voices: []
                };
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const catalog = [...(piperManifest.catalog || []), result.value];
                savePiperManifest({ ...piperManifest, catalog });
                loadPiperModelFiles();
            }
        });
    };

    const handleDeleteCatalogGroup = (groupIdx) => {
        const group = (piperManifest.catalog || [])[groupIdx];
        if (!group) return;
        Swal.fire({
            title: `Xóa nguồn "${group.name}"?`,
            text: `Sẽ xóa ${(group.voices || []).length} voice(s) khỏi manifest.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                savePiperManifest({
                    ...piperManifest,
                    catalog: piperManifest.catalog.filter((_, i) => i !== groupIdx)
                });
            }
        });
    };

    // ═══════ Piper Inline Edit Handlers ═══════
    const handleEditPiperVersion = (vIdx) => {
        let selectedFile = null;
        const ver = (piperManifest.versions || [])[vIdx];
        if (!ver) return;
        Swal.fire({
            title: 'Sửa phiên bản Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version</label>
                    <input id="swal-ev-version" class="swal2-input" value="${ver.version || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Engine File hiện tại</label>
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;padding:8px 12px;background:#f1f5f9;border-radius:6px;font-size:13px">
                        <i class="fas fa-file-archive" style="color:#6366f1"></i>
                        <span style="font-weight:500">${ver.engineFile || '(chưa có)'}</span>
                    </div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Thay file Engine mới (tuỳ chọn)</label>
                    <input id="swal-ev-new-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-ev-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px">Bỏ trống nếu không cần thay file</div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label>
                    <input id="swal-ev-extract" class="swal2-input" value="${ver.engineExtractPath || 'lib/PiperTTS'}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-ev-date" type="date" class="swal2-input" value="${ver.releaseDate || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-ev-log" class="swal2-textarea" style="margin:0;width:100%">${ver.changelog || ''}</textarea>
                    <div id="swal-ev-progress" style="margin-top:10px;display:none">
                        <small id="swal-ev-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-ev-progress-bar" style="background:#10b981;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Lưu',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-ev-new-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-ev-finfo').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB) — <span style="color:#f59e0b;font-weight:600">sẽ đè lên file cũ</span>`;
                    } else {
                        document.getElementById('swal-ev-finfo').textContent = 'Bỏ trống nếu không cần thay file';
                    }
                });
            },
            preConfirm: async () => {
                const version = document.getElementById('swal-ev-version').value.trim();
                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');

                let engineFile = ver.engineFile;

                // Upload file mới nếu có chọn
                if (selectedFile) {
                    const progressDiv = document.getElementById('swal-ev-progress');
                    const progressBar = document.getElementById('swal-ev-progress-bar');
                    const progressText = document.getElementById('swal-ev-progress-text');
                    progressDiv.style.display = 'block';
                    Swal.showLoading();

                    try {
                        await DownloadFileService.uploadFileChunked('Plugin/PiperTTS', selectedFile, (e) => {
                            const pct = Math.round((e.loaded / e.total) * 100);
                            progressBar.style.width = pct + '%';
                            progressText.textContent = `Đang tải lên... ${pct}%`;
                        });
                        engineFile = selectedFile.name;
                        // Auto-cập nhật ngày khi upload file mới
                        document.getElementById('swal-ev-date').value = new Date().toISOString().split('T')[0];
                    } catch (err) {
                        Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                        return;
                    }
                }

                return {
                    version,
                    engineFile,
                    engineExtractPath: document.getElementById('swal-ev-extract').value.trim() || 'lib/PiperTTS',
                    releaseDate: document.getElementById('swal-ev-date').value,
                    changelog: document.getElementById('swal-ev-log').value.trim(),
                    plugins: ver.plugins || []
                };
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const versions = [...(piperManifest.versions || [])];
                versions[vIdx] = { ...versions[vIdx], ...result.value };
                savePiperManifest({ ...piperManifest, versions });
                loadPiperEngineFiles();
            }
        });
    };

    const handleEditPlugin = (vIdx, pIdx) => {
        const plugin = (piperManifest.versions || [])[vIdx]?.plugins?.[pIdx];
        if (!plugin) return;
        Swal.fire({
            title: 'Sửa Plugin',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Plugin ID</label>
                    <input id="swal-ep-id" class="swal2-input" value="${plugin.id || ''}" style="margin:0 0 12px 0;width:100%" readonly>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên</label>
                    <input id="swal-ep-name" class="swal2-input" value="${plugin.name || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File</label>
                    <input id="swal-ep-file" class="swal2-input" value="${plugin.file || ''}" style="margin:0 0 12px 0;width:100%" readonly>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label>
                    <input id="swal-ep-extract" class="swal2-input" value="${plugin.extractPath || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Mô tả</label>
                    <input id="swal-ep-desc" class="swal2-input" value="${plugin.description || ''}" style="margin:0 0 12px 0;width:100%">
                    <div><label style="font-size:12px;display:flex;align-items:center;gap:6px;cursor:pointer">
                        <input type="checkbox" id="swal-ep-req" ${plugin.required ? 'checked' : ''}> Bắt buộc
                    </label></div>
                </div>
            `,
            confirmButtonText: 'Lưu',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            preConfirm: () => ({
                id: plugin.id,
                name: document.getElementById('swal-ep-name').value.trim(),
                file: plugin.file,
                extractPath: document.getElementById('swal-ep-extract').value.trim(),
                description: document.getElementById('swal-ep-desc').value.trim(),
                required: document.getElementById('swal-ep-req').checked
            })
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const versions = [...(piperManifest.versions || [])];
                const plugins = [...(versions[vIdx].plugins || [])];
                plugins[pIdx] = result.value;
                versions[vIdx] = { ...versions[vIdx], plugins };
                savePiperManifest({ ...piperManifest, versions });
            }
        });
    };

    const handleEditCatalogGroup = (gIdx) => {
        const group = (piperManifest.catalog || [])[gIdx];
        if (!group) return;
        Swal.fire({
            title: 'Sửa nguồn Model',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">ID</label>
                    <input id="swal-eg-id" class="swal2-input" value="${group.id || ''}" style="margin:0 0 12px 0;width:100%" readonly>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên</label>
                    <input id="swal-eg-name" class="swal2-input" value="${group.name || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Mô tả</label>
                    <input id="swal-eg-desc" class="swal2-input" value="${group.description || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tác giả</label>
                    <input id="swal-eg-author" class="swal2-input" value="${group.author || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">License</label>
                    <input id="swal-eg-license" class="swal2-input" value="${group.license || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Ngôn ngữ</label>
                    <input id="swal-eg-lang" class="swal2-input" value="${group.language || 'vi'}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Model Extract Path</label>
                    <input id="swal-eg-extract" class="swal2-input" value="${group.modelExtractPath || 'lib/PiperTTS/models'}" style="margin:0 0 12px 0;width:100%">
                    <div><label style="font-size:12px;display:flex;align-items:center;gap:6px;cursor:pointer">
                        <input type="checkbox" id="swal-eg-preprocess" ${group.hasPreprocessing ? 'checked' : ''}> Có text preprocessing
                    </label></div>
                </div>
            `,
            confirmButtonText: 'Lưu',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            preConfirm: () => ({
                name: document.getElementById('swal-eg-name').value.trim(),
                description: document.getElementById('swal-eg-desc').value.trim(),
                author: document.getElementById('swal-eg-author').value.trim(),
                license: document.getElementById('swal-eg-license').value.trim(),
                language: document.getElementById('swal-eg-lang').value.trim() || 'vi',
                modelExtractPath: document.getElementById('swal-eg-extract').value.trim() || 'lib/PiperTTS/models',
                hasPreprocessing: document.getElementById('swal-eg-preprocess').checked
            })
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const catalog = [...(piperManifest.catalog || [])];
                catalog[gIdx] = { ...catalog[gIdx], ...result.value };
                savePiperManifest({ ...piperManifest, catalog });
            }
        });
    };

    const handleEditVoice = (gIdx, vIdx) => {
        const voice = (piperManifest.catalog || [])[gIdx]?.voices?.[vIdx];
        if (!voice) return;
        Swal.fire({
            title: 'Sửa Voice',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên voice</label>
                    <input id="swal-evo-name" class="swal2-input" value="${voice.name || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File</label>
                    <input id="swal-evo-file" class="swal2-input" value="${voice.file || ''}" style="margin:0 0 12px 0;width:100%" readonly>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label>
                    <input id="swal-evo-extract" class="swal2-input" value="${voice.extractPath || 'lib/PiperTTS/models'}" style="margin:0 0 12px 0;width:100%">
                </div>
            `,
            confirmButtonText: 'Lưu',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            preConfirm: () => ({
                name: document.getElementById('swal-evo-name').value.trim(),
                extractPath: document.getElementById('swal-evo-extract').value.trim() || 'lib/PiperTTS/models'
            })
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const catalog = [...(piperManifest.catalog || [])];
                const voices = [...(catalog[gIdx].voices || [])];
                voices[vIdx] = { ...voices[vIdx], ...result.value };
                catalog[gIdx] = { ...catalog[gIdx], voices };
                savePiperManifest({ ...piperManifest, catalog });
            }
        });
    };

    const showAddVoiceToGroupDialog = (groupIdx) => {
        let selectedFile = null;
        Swal.fire({
            title: `Thêm Voice vào "${(piperManifest.catalog || [])[groupIdx]?.name}"`,
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên voice (vd: Calm Woman)</label>
                    <input id="swal-voice-name" class="swal2-input" placeholder="Calm Woman" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Voice (.zip)</label>
                    <input id="swal-voice-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-voice-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <div id="swal-voice-progress" style="margin-top:10px;display:none">
                        <small id="swal-voice-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-voice-progress-bar" style="background:#10b981;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm',
            cancelButtonText: 'Hủy',
            showCancelButton: true,
            focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-voice-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) {
                        document.getElementById('swal-voice-finfo').innerHTML =
                            `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                    }
                });
            },
            preConfirm: async () => {
                const name = document.getElementById('swal-voice-name').value.trim();
                if (!name) return Swal.showValidationMessage('Vui lòng nhập tên voice');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file voice');

                const progressDiv = document.getElementById('swal-voice-progress');
                const progressBar = document.getElementById('swal-voice-progress-bar');
                const progressText = document.getElementById('swal-voice-progress-text');
                progressDiv.style.display = 'block';
                Swal.showLoading();

                try {
                    await DownloadFileService.uploadFileChunked('Plugin/PiperTTS/Voices', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%';
                        progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return { name, file: selectedFile.name, size: selectedFile.size };
                } catch (err) {
                    Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const v = result.value;
                const catalog = [...(piperManifest.catalog || [])];
                catalog[groupIdx] = {
                    ...catalog[groupIdx],
                    voices: [...(catalog[groupIdx].voices || []), { name: v.name, file: v.file, size: v.size }]
                };
                savePiperManifest({ ...piperManifest, catalog });
                loadPiperModelFiles();
            }
        });
    };

    const handleDeleteVoiceFromGroup = (groupIdx, voiceIdx) => {
        const group = (piperManifest.catalog || [])[groupIdx];
        const voice = (group?.voices || [])[voiceIdx];
        if (!voice) return;
        Swal.fire({
            title: `Xóa voice "${voice.name}"?`,
            text: 'Chỉ xóa khỏi manifest, không xóa file.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                const catalog = [...(piperManifest.catalog || [])];
                catalog[groupIdx] = {
                    ...catalog[groupIdx],
                    voices: catalog[groupIdx].voices.filter((_, i) => i !== voiceIdx)
                };
                savePiperManifest({ ...piperManifest, catalog });
            }
        });
    };

    // ═══════ CoquiTTS Version Actions ═══════
    const showAddCoquiVersionDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm phiên bản CoquiTTS Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version (vd: v1)</label>
                    <input id="swal-cq-version" class="swal2-input" placeholder="v1" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Engine (.zip)</label>
                    <input id="swal-cq-engine-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-cq-file-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label>
                    <input id="swal-cq-extract-path" class="swal2-input" placeholder="lib/CoquiTTS" value="lib/CoquiTTS" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-cq-date" type="date" class="swal2-input" value="${new Date().toISOString().split('T')[0]}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-cq-changelog" class="swal2-textarea" placeholder="Mô tả thay đổi..." style="margin:0;width:100%"></textarea>
                    <div id="swal-cq-progress" style="margin-top:10px;display:none">
                        <small id="swal-cq-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden">
                            <div id="swal-cq-progress-bar" style="background:#dc2626;height:100%;width:0%;transition:width 0.3s"></div>
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm', cancelButtonText: 'Hủy', showCancelButton: true, focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-cq-engine-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) document.getElementById('swal-cq-file-info').innerHTML = `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                });
            },
            preConfirm: async () => {
                const version = document.getElementById('swal-cq-version').value.trim();
                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file engine');
                const progressDiv = document.getElementById('swal-cq-progress');
                const progressBar = document.getElementById('swal-cq-progress-bar');
                const progressText = document.getElementById('swal-cq-progress-text');
                progressDiv.style.display = 'block'; Swal.showLoading();
                try {
                    await DownloadFileService.uploadFileChunked('Plugin/CoquiTTS', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        progressBar.style.width = pct + '%'; progressText.textContent = `Đang tải lên... ${pct}%`;
                    });
                    return { version, engineFile: selectedFile.name, engineExtractPath: document.getElementById('swal-cq-extract-path').value.trim() || 'lib/CoquiTTS', releaseDate: document.getElementById('swal-cq-date').value, changelog: document.getElementById('swal-cq-changelog').value.trim() };
                } catch (err) { Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`); }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const v = result.value;
                const filteredVersions = (coquiManifest.versions || []).filter(ver => ver.version !== v.version);
                saveCoquiManifest({ ...coquiManifest, latestVersion: v.version, versions: [v, ...filteredVersions] });
                loadCoquiEngineFiles();
            }
        });
    };

    const handleEditCoquiVersion = (vIdx) => {
        let selectedFile = null;
        const ver = (coquiManifest.versions || [])[vIdx];
        if (!ver) return;
        Swal.fire({
            title: 'Sửa phiên bản CoquiTTS Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version</label>
                    <input id="swal-ecq-version" class="swal2-input" value="${ver.version || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Engine File hiện tại</label>
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;padding:8px 12px;background:#f1f5f9;border-radius:6px;font-size:13px">
                        <i class="fas fa-file-archive" style="color:#dc2626"></i><span style="font-weight:500">${ver.engineFile || '(chưa có)'}</span>
                    </div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Thay file Engine mới (tuỳ chọn)</label>
                    <input id="swal-ecq-new-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-ecq-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px">Bỏ trống nếu không cần thay file</div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label>
                    <input id="swal-ecq-extract" class="swal2-input" value="${ver.engineExtractPath || 'lib/CoquiTTS'}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-ecq-date" type="date" class="swal2-input" value="${ver.releaseDate || ''}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-ecq-log" class="swal2-textarea" style="margin:0;width:100%">${ver.changelog || ''}</textarea>
                    <div id="swal-ecq-progress" style="margin-top:10px;display:none">
                        <small id="swal-ecq-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden"><div id="swal-ecq-progress-bar" style="background:#dc2626;height:100%;width:0%;transition:width 0.3s"></div></div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Lưu', cancelButtonText: 'Hủy', showCancelButton: true, focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-ecq-new-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) document.getElementById('swal-ecq-finfo').innerHTML = `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                });
            },
            preConfirm: async () => {
                const version = document.getElementById('swal-ecq-version').value.trim();
                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                let engineFile = ver.engineFile;
                if (selectedFile) {
                    document.getElementById('swal-ecq-progress').style.display = 'block'; Swal.showLoading();
                    try {
                        await DownloadFileService.uploadFileChunked('Plugin/CoquiTTS', selectedFile, (e) => {
                            const pct = Math.round((e.loaded / e.total) * 100);
                            document.getElementById('swal-ecq-progress-bar').style.width = pct + '%';
                            document.getElementById('swal-ecq-progress-text').textContent = `Đang tải lên... ${pct}%`;
                        });
                        engineFile = selectedFile.name;
                    } catch (err) { Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`); return; }
                }
                return { version, engineFile, engineExtractPath: document.getElementById('swal-ecq-extract').value.trim() || 'lib/CoquiTTS', releaseDate: document.getElementById('swal-ecq-date').value, changelog: document.getElementById('swal-ecq-log').value.trim() };
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const versions = [...(coquiManifest.versions || [])]; versions[vIdx] = { ...versions[vIdx], ...result.value };
                saveCoquiManifest({ ...coquiManifest, versions }); loadCoquiEngineFiles();
            }
        });
    };

    const handleDeleteCoquiVersion = (idx) => {
        Swal.fire({ title: 'Xóa phiên bản?', text: `Xóa "${coquiManifest.versions[idx].version}" khỏi manifest?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Xóa', cancelButtonText: 'Hủy' }).then(result => {
            if (result.isConfirmed) saveCoquiManifest({ ...coquiManifest, versions: coquiManifest.versions.filter((_, i) => i !== idx) });
        });
    };

    const handleSetCoquiLatest = (version) => { saveCoquiManifest({ ...coquiManifest, latestVersion: version }); };

    // CoquiTTS Model Actions
    const showAddCoquiModelDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm CoquiTTS Model',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Tên Model</label>
                    <input id="swal-cqm-name" class="swal2-input" placeholder="vi-vn-default" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Model (.zip)</label>
                    <input id="swal-cqm-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-cqm-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <div id="swal-cqm-progress" style="margin-top:10px;display:none">
                        <small id="swal-cqm-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden"><div id="swal-cqm-progress-bar" style="background:#dc2626;height:100%;width:0%;transition:width 0.3s"></div></div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm', cancelButtonText: 'Hủy', showCancelButton: true, focusConfirm: false,
            didOpen: () => {
                document.getElementById('swal-cqm-file').addEventListener('change', (e) => {
                    selectedFile = e.target.files[0];
                    if (selectedFile) document.getElementById('swal-cqm-finfo').innerHTML = `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`;
                });
            },
            preConfirm: async () => {
                const name = document.getElementById('swal-cqm-name').value.trim();
                if (!name) return Swal.showValidationMessage('Vui lòng nhập tên model');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file model');
                document.getElementById('swal-cqm-progress').style.display = 'block'; Swal.showLoading();
                try {
                    await DownloadFileService.uploadFileChunked('Plugin/CoquiTTS/Models', selectedFile, (e) => {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        document.getElementById('swal-cqm-progress-bar').style.width = pct + '%';
                        document.getElementById('swal-cqm-progress-text').textContent = `Đang tải lên... ${pct}%`;
                    });
                    return { name, file: selectedFile.name, size: selectedFile.size };
                } catch (err) { Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`); }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                saveCoquiManifest({ ...coquiManifest, models: [...(coquiManifest.models || []), result.value] });
                loadCoquiModelFiles();
            }
        });
    };

    const handleDeleteCoquiModel = (mIdx) => {
        Swal.fire({ title: `Xóa model "${coquiManifest.models[mIdx].name}"?`, text: 'Chỉ xóa khỏi manifest.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Xóa', cancelButtonText: 'Hủy' }).then(result => {
            if (result.isConfirmed) saveCoquiManifest({ ...coquiManifest, models: coquiManifest.models.filter((_, i) => i !== mIdx) });
        });
    };

    // ═══════ OmniVoice Version Actions ═══════
    const showAddOmniVoiceVersionDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm phiên bản OmniVoice Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version (vd: v1)</label>
                    <input id="swal-ov-version" class="swal2-input" placeholder="v1" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Engine (.zip)</label>
                    <input id="swal-ov-engine-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-ov-file-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label>
                    <input id="swal-ov-extract-path" class="swal2-input" placeholder="lib/OmniVoice" value="lib/OmniVoice" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-ov-date" type="date" class="swal2-input" value="${new Date().toISOString().split('T')[0]}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-ov-changelog" class="swal2-textarea" placeholder="Mô tả thay đổi..." style="margin:0;width:100%"></textarea>
                    <div id="swal-ov-progress" style="margin-top:10px;display:none">
                        <small id="swal-ov-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden"><div id="swal-ov-progress-bar" style="background:#7c3aed;height:100%;width:0%;transition:width 0.3s"></div></div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm', cancelButtonText: 'Hủy', showCancelButton: true, focusConfirm: false,
            didOpen: () => { document.getElementById('swal-ov-engine-file').addEventListener('change', (e) => { selectedFile = e.target.files[0]; if (selectedFile) document.getElementById('swal-ov-file-info').innerHTML = `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`; }); },
            preConfirm: async () => {
                const version = document.getElementById('swal-ov-version').value.trim();
                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file engine');
                document.getElementById('swal-ov-progress').style.display = 'block'; Swal.showLoading();
                try {
                    await DownloadFileService.uploadFileChunked('Plugin/OmniVoice', selectedFile, (e) => { const pct = Math.round((e.loaded / e.total) * 100); document.getElementById('swal-ov-progress-bar').style.width = pct + '%'; document.getElementById('swal-ov-progress-text').textContent = `Đang tải lên... ${pct}%`; });
                    return { version, engineFile: selectedFile.name, engineExtractPath: document.getElementById('swal-ov-extract-path').value.trim() || 'lib/OmniVoice', releaseDate: document.getElementById('swal-ov-date').value, changelog: document.getElementById('swal-ov-changelog').value.trim() };
                } catch (err) { Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`); }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const v = result.value;
                const filteredVersions = (omniVoiceManifest.versions || []).filter(ver => ver.version !== v.version);
                saveOmniVoiceManifest({ ...omniVoiceManifest, latestVersion: v.version, versions: [v, ...filteredVersions] });
                loadOmniVoiceEngineFiles();
            }
        });
    };

    const handleEditOmniVoiceVersion = (vIdx) => {
        let selectedFile = null;
        const ver = (omniVoiceManifest.versions || [])[vIdx]; if (!ver) return;
        Swal.fire({
            title: 'Sửa phiên bản OmniVoice Engine',
            html: `<div style="text-align:left"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version</label><input id="swal-eov-version" class="swal2-input" value="${ver.version || ''}" style="margin:0 0 12px 0;width:100%"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Engine File hiện tại</label><div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;padding:8px 12px;background:#f1f5f9;border-radius:6px;font-size:13px"><i class="fas fa-file-archive" style="color:#7c3aed"></i><span style="font-weight:500">${ver.engineFile || '(chưa có)'}</span></div><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Thay file Engine mới (tuỳ chọn)</label><input id="swal-eov-new-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%"><div id="swal-eov-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px">Bỏ trống nếu không cần thay file</div><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label><input id="swal-eov-extract" class="swal2-input" value="${ver.engineExtractPath || 'lib/OmniVoice'}" style="margin:0 0 12px 0;width:100%"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label><input id="swal-eov-date" type="date" class="swal2-input" value="${ver.releaseDate || ''}" style="margin:0 0 12px 0;width:100%"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label><textarea id="swal-eov-log" class="swal2-textarea" style="margin:0;width:100%">${ver.changelog || ''}</textarea><div id="swal-eov-progress" style="margin-top:10px;display:none"><small id="swal-eov-progress-text">Đang tải lên... 0%</small><div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden"><div id="swal-eov-progress-bar" style="background:#7c3aed;height:100%;width:0%;transition:width 0.3s"></div></div></div></div>`,
            confirmButtonText: 'Lưu', cancelButtonText: 'Hủy', showCancelButton: true, focusConfirm: false,
            didOpen: () => { document.getElementById('swal-eov-new-file').addEventListener('change', (e) => { selectedFile = e.target.files[0]; if (selectedFile) document.getElementById('swal-eov-finfo').innerHTML = `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`; }); },
            preConfirm: async () => {
                const version = document.getElementById('swal-eov-version').value.trim();
                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                let engineFile = ver.engineFile;
                if (selectedFile) { document.getElementById('swal-eov-progress').style.display = 'block'; Swal.showLoading(); try { await DownloadFileService.uploadFileChunked('Plugin/OmniVoice', selectedFile, (e) => { const pct = Math.round((e.loaded / e.total) * 100); document.getElementById('swal-eov-progress-bar').style.width = pct + '%'; document.getElementById('swal-eov-progress-text').textContent = `Đang tải lên... ${pct}%`; }); engineFile = selectedFile.name; } catch (err) { Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`); return; } }
                return { version, engineFile, engineExtractPath: document.getElementById('swal-eov-extract').value.trim() || 'lib/OmniVoice', releaseDate: document.getElementById('swal-eov-date').value, changelog: document.getElementById('swal-eov-log').value.trim() };
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => { if (result.isConfirmed && result.value) { const versions = [...(omniVoiceManifest.versions || [])]; versions[vIdx] = { ...versions[vIdx], ...result.value }; saveOmniVoiceManifest({ ...omniVoiceManifest, versions }); loadOmniVoiceEngineFiles(); } });
    };

    const handleDeleteOmniVoiceVersion = (idx) => {
        Swal.fire({ title: 'Xóa phiên bản?', text: `Xóa "${omniVoiceManifest.versions[idx].version}" khỏi manifest?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Xóa', cancelButtonText: 'Hủy' }).then(result => {
            if (result.isConfirmed) saveOmniVoiceManifest({ ...omniVoiceManifest, versions: omniVoiceManifest.versions.filter((_, i) => i !== idx) });
        });
    };

    const handleSetOmniVoiceLatest = (version) => { saveOmniVoiceManifest({ ...omniVoiceManifest, latestVersion: version }); };

    // ═══════ VocalRemover Version Actions ═══════
    const showAddVocalRemoverVersionDialog = () => {
        let selectedFile = null;
        Swal.fire({
            title: 'Thêm phiên bản VocalRemover Engine',
            html: `
                <div style="text-align:left">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version (vd: v1)</label>
                    <input id="swal-vr-version" class="swal2-input" placeholder="v1" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">File Engine (.zip)</label>
                    <input id="swal-vr-engine-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%">
                    <div id="swal-vr-file-info" style="font-size:12px;color:#64748b;margin-bottom:12px"></div>
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label>
                    <input id="swal-vr-extract-path" class="swal2-input" placeholder="lib/VocalRemover" value="lib/VocalRemover" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label>
                    <input id="swal-vr-date" type="date" class="swal2-input" value="${new Date().toISOString().split('T')[0]}" style="margin:0 0 12px 0;width:100%">
                    <label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label>
                    <textarea id="swal-vr-changelog" class="swal2-textarea" placeholder="Mô tả thay đổi..." style="margin:0;width:100%"></textarea>
                    <div id="swal-vr-progress" style="margin-top:10px;display:none">
                        <small id="swal-vr-progress-text">Đang tải lên... 0%</small>
                        <div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden"><div id="swal-vr-progress-bar" style="background:#0891b2;height:100%;width:0%;transition:width 0.3s"></div></div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Tải lên & Thêm', cancelButtonText: 'Hủy', showCancelButton: true, focusConfirm: false,
            didOpen: () => { document.getElementById('swal-vr-engine-file').addEventListener('change', (e) => { selectedFile = e.target.files[0]; if (selectedFile) document.getElementById('swal-vr-file-info').innerHTML = `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`; }); },
            preConfirm: async () => {
                const version = document.getElementById('swal-vr-version').value.trim();
                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                if (!selectedFile) return Swal.showValidationMessage('Vui lòng chọn file engine');
                document.getElementById('swal-vr-progress').style.display = 'block'; Swal.showLoading();
                try {
                    await DownloadFileService.uploadFileChunked('Plugin/VocalRemover', selectedFile, (e) => { const pct = Math.round((e.loaded / e.total) * 100); document.getElementById('swal-vr-progress-bar').style.width = pct + '%'; document.getElementById('swal-vr-progress-text').textContent = `Đang tải lên... ${pct}%`; });
                    return { version, engineFile: selectedFile.name, engineExtractPath: document.getElementById('swal-vr-extract-path').value.trim() || 'lib/VocalRemover', releaseDate: document.getElementById('swal-vr-date').value, changelog: document.getElementById('swal-vr-changelog').value.trim() };
                } catch (err) { Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`); }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const v = result.value;
                const filteredVersions = (vocalRemoverManifest.versions || []).filter(ver => ver.version !== v.version);
                saveVocalRemoverManifest({ ...vocalRemoverManifest, latestVersion: v.version, versions: [v, ...filteredVersions] });
                loadVocalRemoverEngineFiles();
            }
        });
    };

    const handleEditVocalRemoverVersion = (vIdx) => {
        let selectedFile = null;
        const ver = (vocalRemoverManifest.versions || [])[vIdx]; if (!ver) return;
        Swal.fire({
            title: 'Sửa phiên bản VocalRemover Engine',
            html: `<div style="text-align:left"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Version</label><input id="swal-evr-version" class="swal2-input" value="${ver.version || ''}" style="margin:0 0 12px 0;width:100%"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Engine File hiện tại</label><div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;padding:8px 12px;background:#f1f5f9;border-radius:6px;font-size:13px"><i class="fas fa-file-archive" style="color:#0891b2"></i><span style="font-weight:500">${ver.engineFile || '(chưa có)'}</span></div><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Thay file Engine mới (tuỳ chọn)</label><input id="swal-evr-new-file" type="file" accept=".zip" class="swal2-file" style="margin:0 0 4px 0;width:100%"><div id="swal-evr-finfo" style="font-size:12px;color:#64748b;margin-bottom:12px">Bỏ trống nếu không cần thay file</div><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Extract Path</label><input id="swal-evr-extract" class="swal2-input" value="${ver.engineExtractPath || 'lib/VocalRemover'}" style="margin:0 0 12px 0;width:100%"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Release Date</label><input id="swal-evr-date" type="date" class="swal2-input" value="${ver.releaseDate || ''}" style="margin:0 0 12px 0;width:100%"><label style="font-weight:600;font-size:13px;margin-bottom:4px;display:block">Changelog</label><textarea id="swal-evr-log" class="swal2-textarea" style="margin:0;width:100%">${ver.changelog || ''}</textarea><div id="swal-evr-progress" style="margin-top:10px;display:none"><small id="swal-evr-progress-text">Đang tải lên... 0%</small><div style="background:#e2e8f0;border-radius:4px;height:8px;margin-top:4px;overflow:hidden"><div id="swal-evr-progress-bar" style="background:#0891b2;height:100%;width:0%;transition:width 0.3s"></div></div></div></div>`,
            confirmButtonText: 'Lưu', cancelButtonText: 'Hủy', showCancelButton: true, focusConfirm: false,
            didOpen: () => { document.getElementById('swal-evr-new-file').addEventListener('change', (e) => { selectedFile = e.target.files[0]; if (selectedFile) document.getElementById('swal-evr-finfo').innerHTML = `<i class="fas fa-file-archive"></i> ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`; }); },
            preConfirm: async () => {
                const version = document.getElementById('swal-evr-version').value.trim();
                if (!version) return Swal.showValidationMessage('Vui lòng nhập version');
                let engineFile = ver.engineFile;
                if (selectedFile) { document.getElementById('swal-evr-progress').style.display = 'block'; Swal.showLoading(); try { await DownloadFileService.uploadFileChunked('Plugin/VocalRemover', selectedFile, (e) => { const pct = Math.round((e.loaded / e.total) * 100); document.getElementById('swal-evr-progress-bar').style.width = pct + '%'; document.getElementById('swal-evr-progress-text').textContent = `Đang tải lên... ${pct}%`; }); engineFile = selectedFile.name; } catch (err) { Swal.showValidationMessage(`Lỗi tải lên: ${err.message || err}`); return; } }
                return { version, engineFile, engineExtractPath: document.getElementById('swal-evr-extract').value.trim() || 'lib/VocalRemover', releaseDate: document.getElementById('swal-evr-date').value, changelog: document.getElementById('swal-evr-log').value.trim() };
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then(result => { if (result.isConfirmed && result.value) { const versions = [...(vocalRemoverManifest.versions || [])]; versions[vIdx] = { ...versions[vIdx], ...result.value }; saveVocalRemoverManifest({ ...vocalRemoverManifest, versions }); loadVocalRemoverEngineFiles(); } });
    };

    const handleDeleteVocalRemoverVersion = (idx) => {
        Swal.fire({ title: 'Xóa phiên bản?', text: `Xóa "${vocalRemoverManifest.versions[idx].version}" khỏi manifest?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Xóa', cancelButtonText: 'Hủy' }).then(result => {
            if (result.isConfirmed) saveVocalRemoverManifest({ ...vocalRemoverManifest, versions: vocalRemoverManifest.versions.filter((_, i) => i !== idx) });
        });
    };

    const handleSetVocalRemoverLatest = (version) => { saveVocalRemoverManifest({ ...vocalRemoverManifest, latestVersion: version }); };

    // ═══════ File Upload/Delete ═══════
    const handleFileUpload = (folder, event, onComplete) => {
        const filesToUpload = event.target.files;
        if (!filesToUpload.length) return;

        Array.from(filesToUpload).forEach(file => {
            const progressKey = `${folder}/${file.name}`;
            DownloadFileService.uploadFileChunked(folder, file, (progressEvent) => {
                const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                setUploadProgress(prev => ({ ...prev, [progressKey]: progress }));
            })
                .then(() => {
                    setUploadProgress(prev => {
                        const next = { ...prev };
                        delete next[progressKey];
                        return next;
                    });
                    showToast(`${file.name} đã tải lên thành công`, '#4caf50');
                    if (onComplete) onComplete();
                })
                .catch(() => {
                    showToast(`Lỗi tải lên ${file.name}`, '#f44336');
                });
        });
    };

    const handleDeleteFile = (folder, fileName, onComplete) => {
        Swal.fire({
            title: 'Xóa file?',
            text: `Xóa "${fileName}" khỏi server?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then(result => {
            if (result.isConfirmed) {
                DownloadFileService.deleteFiles(folder, [fileName])
                    .then(() => {
                        showToast('Đã xóa file', '#4caf50');
                        if (onComplete) onComplete();
                    })
                    .catch(() => showToast('Lỗi xóa file', '#f44336'));
            }
        });
    };

    // ═══════ AI Models ═══════
    const loadAiSettings = async () => {
        setAiLoading(true);
        try {
            const res = await settingService.getAll();
            if (res.data?.success) {
                const map = {};
                res.data.settings
                    .filter(s => s.key.startsWith('aiModels_'))
                    .forEach(s => { map[s.key] = s.value; });
                setAiSettings(map);
            }
        } catch (err) {
            console.error('Lỗi lấy AI settings:', err);
        } finally {
            setAiLoading(false);
        }
    };

    const startEdit = useCallback((key) => {
        const models = aiSettings[key] || [];
        setEditModels(JSON.parse(JSON.stringify(models)));
        setEditingKey(key);
    }, [aiSettings]);

    const cancelEdit = () => { setEditingKey(null); setEditModels([]); };

    const saveAiEdit = async () => {
        if (!editingKey) return;
        const cleaned = editModels.filter(m => m.name?.trim() && (m.model?.trim() || m.repo?.trim()));
        setAiSaving(prev => ({ ...prev, [editingKey]: true }));
        try {
            const res = await settingService.update(editingKey, cleaned);
            if (res.data?.success) {
                setAiSettings(prev => ({ ...prev, [editingKey]: cleaned }));
                setEditingKey(null);
                setEditModels([]);
                showToast('Đã lưu AI model', '#4caf50');
            }
        } catch {
            showToast('Lỗi lưu AI model', '#f44336');
        } finally {
            setAiSaving(prev => ({ ...prev, [editingKey]: false }));
        }
    };

    const addAiModel = () => setEditModels(prev => [...prev, { name: '', model: '' }]);
    const removeAiModel = (idx) => setEditModels(prev => prev.filter((_, i) => i !== idx));
    const updateAiModel = (idx, field, value) => setEditModels(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
    const moveAiModel = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= editModels.length) return;
        setEditModels(prev => {
            const arr = [...prev];
            [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
            return arr;
        });
    };

    // ═══════ File Browser Component ═══════
    const FileBrowser = ({ folder, files, onRefresh, label }) => (
        <div className="drm-file-browser" style={{ borderTop: '1px solid #e2e8f0', padding: '12px 20px', backgroundColor: '#f8fafc' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <strong style={{ fontSize: 13, color: '#64748b' }}>
                    <i className="fas fa-folder" style={{ marginRight: 6 }} />
                    {label || folder + '/'}
                </strong>
                <label style={{ cursor: 'pointer', margin: 0 }}>
                    <input type="file" multiple style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(folder, e, onRefresh)} />
                    <Button color="info" size="sm" tag="span" className="drm-primary-action">
                        <i className="fas fa-upload" style={{ marginRight: 4 }} /> Upload
                    </Button>
                </label>
            </div>
            {Object.entries(uploadProgress)
                .filter(([key]) => key.startsWith(folder + '/'))
                .map(([key, progress]) => (
                    <div key={key} style={{ marginBottom: 6 }}>
                        <small>{key.split('/').pop()}</small>
                        <Progress value={progress} style={{ height: 6 }} />
                    </div>
                ))}
            {(files || []).filter(f => f.type === 'file').map(file => (
                <div key={file.name} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13
                }}>
                    <span style={{ fontFamily: 'monospace' }}>
                        <i className="fas fa-file-archive" style={{ marginRight: 6, color: '#6366f1' }} />
                        {file.name}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ color: '#94a3b8' }}>{formatSize(file.size)}</span>
                        <Button color="link" size="sm" style={{ color: '#ef4444', padding: 0 }}
                            onClick={() => handleDeleteFile(folder, file.name, onRefresh)}>
                            <i className="fas fa-trash" />
                        </Button>
                    </div>
                </div>
            ))}
            {(!files || files.filter(f => f.type === 'file').length === 0) && (
                <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>Chưa có file nào</div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="content dhb-render-model-page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 100 }}>
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <div className="content dhb-render-model-page">
            {/* ═══════ Tab Bar ═══════ */}
            <div className="drm-tab-bar">
                <div className={`drm-tab-item ${activeTab === 'subtitle' ? 'active' : ''}`} onClick={() => setActiveTab('subtitle')}>
                    <i className="fas fa-closed-captioning" /> Auto Subtitle
                </div>
                <div className={`drm-tab-item ${activeTab === 'translate' ? 'active' : ''}`} onClick={() => setActiveTab('translate')}>
                    <i className="fas fa-language" /> Translate
                </div>
                <div className={`drm-tab-item ${activeTab === 'speech' ? 'active' : ''}`} onClick={() => setActiveTab('speech')}>
                    <i className="fas fa-microphone-alt" /> Speech
                </div>
                <div className={`drm-tab-item ${activeTab === 'vocalremover' ? 'active' : ''}`} onClick={() => setActiveTab('vocalremover')}>
                    <i className="fas fa-music" /> Vocal Remover
                </div>
                <div className={`drm-tab-item ${activeTab === 'content' ? 'active' : ''}`} onClick={() => setActiveTab('content')}>
                    <i className="fas fa-pen-fancy" /> Content Generation
                </div>
            </div>

            {/* ═══════ TAB: Auto Subtitle ═══════ */}
            {activeTab === 'subtitle' && (<>
                {/* ═══════ Whisper Engine Card ═══════ */}
                <Card className="drm-card drm-card-whisper" style={{ borderRadius: 15, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', marginBottom: 20 }}>
                    <CardHeader className="drm-card-header" style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        padding: '16px 24px',
                        borderRadius: '15px 15px 0 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                            <h4 style={{ color: '#fff', margin: 0, fontWeight: 700 }}>
                                <i className="fas fa-brain" style={{ marginRight: 10 }} />
                                Whisper Engine
                            </h4>
                            <Badge color="info" style={{ fontSize: 11, padding: '4px 8px' }}>
                                XXL: {manifest?.latestVersionXXL || '—'}
                            </Badge>
                            <Badge color="warning" style={{ fontSize: 11, padding: '4px 8px' }}>
                                Stable TS: {manifest?.latestVersionStableTS || '—'}
                            </Badge>
                            {saving && <Spinner size="sm" style={{ color: '#fff' }} />}
                        </div>
                    </CardHeader>
                    <CardBody className="drm-card-body" style={{ padding: '20px 24px' }}>
                        <Row>
                            {/* ── Left: Versions ── */}
                            <Col md="6">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                    <div>
                                        <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <i className="fas fa-code-branch" style={{ color: '#6366f1' }} />
                                            Phiên bản Engine
                                        </h6>
                                        <small style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>
                                            <i className="fas fa-folder" style={{ marginRight: 4 }} />Plugin/Whisper/
                                        </small>
                                    </div>
                                    <Button color="primary" size="sm" className="drm-primary-action" onClick={showAddVersionDialog}>
                                        <i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm
                                    </Button>
                                </div>
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                    <Table hover responsive size="sm" style={{ margin: 0 }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}>
                                            <tr>
                                                <th style={{ padding: '8px 14px' }}>Version</th>
                                                <th>Engine</th>
                                                <th>Engine File</th>
                                                <th>Size</th>
                                                <th style={{ textAlign: 'center' }}>File</th>
                                                <th>Ngày</th>
                                                <th style={{ width: 90, textAlign: 'center' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(manifest?.versions || []).map((ver, vIdx) => {
                                                const isLatest = (ver.engineId === 'xxl' && ver.version === manifest.latestVersionXXL)
                                                    || (ver.engineId === 'stable-ts' && ver.version === manifest.latestVersionStableTS);
                                                return (
                                                    <tr key={`${ver.engineId}-${ver.version}`}>
                                                        <td style={{ padding: '6px 14px', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                                                            {ver.version}
                                                            {isLatest && (
                                                                <Badge color="success" style={{ marginLeft: 6, fontSize: 9 }}>Latest</Badge>
                                                            )}
                                                        </td>
                                                        <td style={{ fontSize: 12 }}>
                                                            <Badge color={ver.engineId === 'stable-ts' ? 'warning' : 'info'} style={{ fontSize: 10 }}>
                                                                {ver.engineId === 'stable-ts' ? 'Stable TS' : 'XXL'}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{ver.engineFile}</td>
                                                        <td style={{ fontSize: 12 }}>{formatSize(versionFiles.find(f => f.name === ver.engineFile)?.size)}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <Badge color={ver.exist ? 'success' : 'danger'} style={{ fontSize: 10 }}>
                                                                <i className={`fas ${ver.exist ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
                                                            </Badge>
                                                        </td>
                                                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{ver.releaseDate || '—'}</td>
                                                        <td className="drm-action-cell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                            {!isLatest && (
                                                                <Button color="link" size="sm" style={{ padding: '0 4px', color: '#f59e0b', fontSize: 11 }}
                                                                    onClick={() => {
                                                                        const key = ver.engineId === 'stable-ts' ? 'latestVersionStableTS' : 'latestVersionXXL';
                                                                        saveManifest({ ...manifest, [key]: ver.version });
                                                                    }} title="Đặt làm Latest">
                                                                    <i className="fas fa-star" />
                                                                </Button>
                                                            )}
                                                            <Button color="link" size="sm" style={{ padding: '0 4px', color: '#ef4444', fontSize: 11 }}
                                                                onClick={() => handleDeleteVersion(vIdx)} title="Xóa">
                                                                <i className="fas fa-trash" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {(!manifest?.versions || manifest.versions.length === 0) && (
                                                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 16, fontSize: 13 }}>Chưa có phiên bản</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>

                            {/* ── Right: Shared Models ── */}
                            <Col md="6">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                    <div>
                                        <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <i className="fas fa-cubes" style={{ color: '#f59e0b' }} />
                                            Model Weights (dùng chung)
                                        </h6>
                                        <small style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>
                                            <i className="fas fa-folder" style={{ marginRight: 4 }} />Plugin/Whisper/Models/
                                        </small>
                                    </div>
                                    <Button color="warning" size="sm" className="drm-primary-action" onClick={showAddModelDialog}>
                                        <i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm
                                    </Button>
                                </div>
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                    <Table hover responsive size="sm" style={{ margin: 0 }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}>
                                            <tr>
                                                <th style={{ padding: '8px 14px' }}>Model</th>
                                                <th>File</th>
                                                <th>Size</th>
                                                <th style={{ textAlign: 'center' }}>File</th>
                                                <th style={{ width: 50, textAlign: 'center' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(manifest?.models || []).map((model, mIdx) => (
                                                <tr key={mIdx}>
                                                    <td style={{ padding: '6px 14px', fontWeight: 600, fontSize: 13 }}>{model.name}</td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{model.file}</td>
                                                    <td style={{ fontSize: 12 }}>{formatSize(model.size)}</td>
                                                    <td className="drm-action-cell" style={{ textAlign: 'center' }}>
                                                        <Badge color={model.exist ? 'success' : 'danger'} style={{ fontSize: 10 }}>
                                                            <i className={`fas ${model.exist ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
                                                        </Badge>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Button color="link" style={{ color: '#ef4444', padding: 0 }}
                                                            onClick={() => handleDeleteSharedModel(mIdx)}>
                                                            <i className="fas fa-times" style={{ fontSize: 12 }} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!manifest?.models || manifest.models.length === 0) && (
                                                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 16, fontSize: 13 }}>Chưa có model</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

            </>)}

            {/* ═══════ TAB: Vocal Remover ═══════ */}
            {activeTab === 'vocalremover' && (<>
                <Card className="drm-card" style={{ borderRadius: 15, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', marginBottom: 20 }}>
                    <CardHeader className="drm-card-header" style={{ background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)', padding: '16px 24px', borderRadius: '15px 15px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h4 style={{ color: '#fff', margin: 0, fontWeight: 700 }}><i className="fas fa-music" style={{ marginRight: 10 }} />VocalRemover Engine</h4>
                            <Badge color="light" style={{ fontSize: 13, color: '#0e7490', fontWeight: 700, padding: '5px 10px' }}>{vocalRemoverManifest?.latestVersion || '—'}</Badge>
                            {vocalRemoverSaving && <Spinner size="sm" style={{ color: '#fff' }} />}
                        </div>
                    </CardHeader>
                    <CardBody style={{ padding: '20px 24px' }}>
                        {vocalRemoverLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spinner size="sm" color="info" /></div> : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                    <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <i className="fas fa-code-branch" style={{ color: '#0891b2' }} />Phiên bản Engine
                                    </h6>
                                    <Button color="info" size="sm" className="drm-primary-action" onClick={showAddVocalRemoverVersionDialog}><i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm</Button>
                                </div>
                                <div className="drm-engine-table">
                                    <Table hover responsive size="sm" style={{ margin: 0 }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}><tr><th style={{ padding: '8px 14px' }}>Version</th><th>File</th><th style={{ textAlign: 'center', width: 40 }}><i className="fas fa-check-circle" title="Tồn tại" /></th><th>Ngày</th><th style={{ width: 100, textAlign: 'center' }}></th></tr></thead>
                                        <tbody>
                                            {(vocalRemoverManifest?.versions || []).map((ver, vIdx) => (
                                                <tr key={ver.version} style={{ borderLeft: ver.version === vocalRemoverManifest.latestVersion ? '3px solid #0891b2' : '3px solid transparent' }}>
                                                    <td style={{ padding: '6px 14px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
                                                        {ver.version}
                                                        {ver.version === vocalRemoverManifest.latestVersion && <Badge color="success" style={{ marginLeft: 6, fontSize: 9 }}>Latest</Badge>}
                                                    </td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{ver.engineFile}</td>
                                                    <td style={{ textAlign: 'center' }}><span style={{ color: ver.exist ? '#10b981' : '#ef4444', fontSize: 14 }}><i className={`fas ${ver.exist ? 'fa-check-circle' : 'fa-times-circle'}`} /></span></td>
                                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{ver.releaseDate || '—'}</td>
                                                    <td className="drm-action-cell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#3b82f6' }} onClick={() => handleEditVocalRemoverVersion(vIdx)} title="Sửa"><i className="fas fa-pen" style={{ fontSize: 10 }} /></Button>
                                                        {ver.version !== vocalRemoverManifest.latestVersion && <Button color="link" size="sm" style={{ padding: '0 3px', color: '#f59e0b' }} onClick={() => handleSetVocalRemoverLatest(ver.version)} title="Đặt Latest"><i className="fas fa-star" style={{ fontSize: 10 }} /></Button>}
                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#ef4444' }} onClick={() => handleDeleteVocalRemoverVersion(vIdx)} title="Xóa"><i className="fas fa-trash" style={{ fontSize: 10 }} /></Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!vocalRemoverManifest?.versions || vocalRemoverManifest.versions.length === 0) && (
                                                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 20, fontSize: 13 }}>Chưa có phiên bản</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </>)}

            {/* ═══════ TAB: Speech ═══════ */}
            {activeTab === 'speech' && (<>

                {/* ═══════ CoquiTTS Engine Card ═══════ */}
                <Card className="drm-card" style={{ borderRadius: 15, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', marginBottom: 20 }}>
                    <CardHeader className="drm-card-header" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', padding: '16px 24px', borderRadius: '15px 15px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h4 style={{ color: '#fff', margin: 0, fontWeight: 700 }}><i className="fas fa-comment-dots" style={{ marginRight: 10 }} />CoquiTTS Engine</h4>
                            <Badge color="light" style={{ fontSize: 13, color: '#dc2626', fontWeight: 700, padding: '5px 10px' }}>{coquiManifest?.latestVersion || '—'}</Badge>
                            {coquiSaving && <Spinner size="sm" style={{ color: '#fff' }} />}
                        </div>
                    </CardHeader>
                    <CardBody style={{ padding: '20px 24px' }}>
                        {coquiLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spinner size="sm" color="danger" /></div> : (
                            <Row>
                                <Col md="6">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                        <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><i className="fas fa-code-branch" style={{ color: '#dc2626' }} />Engine Versions</h6>
                                        <Button color="danger" size="sm" className="drm-primary-action" onClick={showAddCoquiVersionDialog}><i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm</Button>
                                    </div>
                                    <div className="drm-engine-table">
                                        <Table hover responsive size="sm" style={{ margin: 0 }}>
                                            <thead style={{ backgroundColor: '#f8fafc' }}><tr><th style={{ padding: '8px 14px' }}>Version</th><th>File</th><th style={{ textAlign: 'center', width: 40 }}><i className="fas fa-check-circle" /></th><th>Ngày</th><th style={{ width: 100, textAlign: 'center' }}></th></tr></thead>
                                            <tbody>
                                                {(coquiManifest?.versions || []).map((ver, vIdx) => (
                                                    <tr key={ver.version} style={{ borderLeft: ver.version === coquiManifest.latestVersion ? '3px solid #dc2626' : '3px solid transparent' }}>
                                                        <td style={{ padding: '6px 14px', fontWeight: 700, fontSize: 13 }}>{ver.version}{ver.version === coquiManifest.latestVersion && <Badge color="success" style={{ marginLeft: 6, fontSize: 9 }}>Latest</Badge>}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{ver.engineFile}</td>
                                                        <td style={{ textAlign: 'center' }}><span style={{ color: ver.exist ? '#10b981' : '#ef4444', fontSize: 14 }}><i className={`fas ${ver.exist ? 'fa-check-circle' : 'fa-times-circle'}`} /></span></td>
                                                        <td style={{ fontSize: 12 }}>{ver.releaseDate || '—'}</td>
                                                        <td className="drm-action-cell" style={{ textAlign: 'center' }}>
                                                            <Button color="link" size="sm" style={{ padding: '0 3px', color: '#3b82f6' }} onClick={() => handleEditCoquiVersion(vIdx)}><i className="fas fa-pen" style={{ fontSize: 10 }} /></Button>
                                                            {ver.version !== coquiManifest.latestVersion && <Button color="link" size="sm" style={{ padding: '0 3px', color: '#f59e0b' }} onClick={() => handleSetCoquiLatest(ver.version)}><i className="fas fa-star" style={{ fontSize: 10 }} /></Button>}
                                                            <Button color="link" size="sm" style={{ padding: '0 3px', color: '#ef4444' }} onClick={() => handleDeleteCoquiVersion(vIdx)}><i className="fas fa-trash" style={{ fontSize: 10 }} /></Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(!coquiManifest?.versions || coquiManifest.versions.length === 0) && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 20, fontSize: 13 }}>Chưa có phiên bản</td></tr>}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                        <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><i className="fas fa-cubes" style={{ color: '#dc2626' }} />Models</h6>
                                        <Button color="danger" size="sm" className="drm-primary-action" onClick={showAddCoquiModelDialog}><i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm</Button>
                                    </div>
                                    <div className="drm-engine-table">
                                        <Table hover responsive size="sm" style={{ margin: 0 }}>
                                            <thead style={{ backgroundColor: '#f8fafc' }}><tr><th style={{ padding: '8px 14px' }}>Model</th><th>File</th><th>Size</th><th style={{ textAlign: 'center', width: 40 }}><i className="fas fa-check-circle" /></th><th style={{ width: 50 }}></th></tr></thead>
                                            <tbody>
                                                {(coquiManifest?.models || []).map((model, mIdx) => (
                                                    <tr key={mIdx}>
                                                        <td style={{ padding: '6px 14px', fontWeight: 600, fontSize: 13 }}>{model.name}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{model.file}</td>
                                                        <td style={{ fontSize: 12 }}>{formatSize(model.size)}</td>
                                                        <td style={{ textAlign: 'center' }}><span style={{ color: model.exist ? '#10b981' : '#ef4444', fontSize: 14 }}><i className={`fas ${model.exist ? 'fa-check-circle' : 'fa-times-circle'}`} /></span></td>
                                                        <td style={{ textAlign: 'center' }}><Button color="link" style={{ color: '#ef4444', padding: 0 }} onClick={() => handleDeleteCoquiModel(mIdx)}><i className="fas fa-times" style={{ fontSize: 12 }} /></Button></td>
                                                    </tr>
                                                ))}
                                                {(!coquiManifest?.models || coquiManifest.models.length === 0) && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 16, fontSize: 13 }}>Chưa có model</td></tr>}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </CardBody>
                </Card>

                {/* ═══════ Piper TTS — Outer Card ═══════ */}
                <Card className="drm-card drm-card-piper" style={{ borderRadius: 15, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', marginBottom: 20 }}>
                    <CardHeader className="drm-card-header" style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        padding: '16px 24px',
                        borderRadius: '15px 15px 0 0',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h4 style={{ color: '#fff', margin: 0, fontWeight: 700 }}>
                                <i className="fas fa-volume-up" style={{ marginRight: 10 }} />
                                Piper TTS Engine
                            </h4>
                            <Badge color="light" style={{ fontSize: 13, color: '#059669', fontWeight: 700, padding: '5px 10px' }}>
                                {piperManifest?.latestVersion || '—'}
                            </Badge>
                            {piperSaving && <Spinner size="sm" style={{ color: '#fff' }} />}
                        </div>
                    </CardHeader>
                    <CardBody className="drm-card-body" style={{ padding: '20px' }}>
                        {piperLoading ? (
                            <div style={{ textAlign: 'center', padding: 40 }}><Spinner size="sm" color="success" /></div>
                        ) : (
                            <Row>
                                {/* ── Left: Engine Versions & Plugins ── */}
                                <Col md="6">
                                    <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <i className="fas fa-cogs" style={{ color: '#10b981' }} />
                                            Engine Versions
                                            <Badge color="light" style={{ fontSize: 10, color: '#64748b' }}>
                                                {(piperManifest?.versions || []).length}
                                            </Badge>
                                        </h6>
                                        <Button color="success" size="sm" className="drm-primary-action" style={{ fontSize: 11 }} onClick={showAddPiperVersionDialog}>
                                            <i className="fas fa-plus" style={{ marginRight: 4 }} />Version
                                        </Button>
                                    </div>
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                        <Table hover responsive size="sm" style={{ margin: 0, fontSize: 12 }}>
                                            <thead style={{ backgroundColor: '#f8fafc' }}>
                                                <tr>
                                                    <th style={{ padding: '8px 14px', width: 80 }}>Version</th>
                                                    <th>File</th>
                                                    <th style={{ textAlign: 'center', width: 40 }}>
                                                        <i className="fas fa-check-circle" title="Tồn tại" />
                                                    </th>
                                                    <th style={{ width: 80 }}>Ngày</th>
                                                    <th style={{ width: 100, textAlign: 'center' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(piperManifest?.versions || []).map((ver, vIdx) => {
                                                    const cudaPlugin = (ver.plugins || []).find(p => p.id === 'cuda');
                                                    return (
                                                        <React.Fragment key={ver.version}>
                                                            {/* Engine row */}
                                                            <tr style={{ borderLeft: ver.version === piperManifest.latestVersion ? '3px solid #10b981' : '3px solid transparent' }}>
                                                                <td style={{ padding: '6px 14px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                    {ver.version}
                                                                    {ver.version === piperManifest.latestVersion && (
                                                                        <Badge color="success" style={{ marginLeft: 4, fontSize: 8, verticalAlign: 'middle' }}>Latest</Badge>
                                                                    )}
                                                                </td>
                                                                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                                                                    <i className="fas fa-microchip" style={{ color: '#64748b', fontSize: 10, marginRight: 4 }} />
                                                                    {ver.engineFile}
                                                                </td>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <span style={{ color: ver.exist ? '#10b981' : '#ef4444', fontSize: 14 }}>
                                                                        <i className={`fas ${ver.exist ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
                                                                    </span>
                                                                </td>
                                                                <td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{ver.releaseDate || '—'}</td>
                                                                <td className="drm-action-cell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                    <Button color="link" size="sm" style={{ padding: '0 3px', color: '#3b82f6' }}
                                                                        onClick={() => handleEditPiperVersion(vIdx)} title="Sửa">
                                                                        <i className="fas fa-pen" style={{ fontSize: 10 }} />
                                                                    </Button>
                                                                    {ver.version !== piperManifest.latestVersion && (
                                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#f59e0b' }}
                                                                            onClick={() => handleSetPiperLatest(ver.version)} title="Đặt Latest">
                                                                            <i className="fas fa-star" style={{ fontSize: 10 }} />
                                                                        </Button>
                                                                    )}
                                                                    <Button color="link" size="sm" style={{ padding: '0 3px', color: '#ef4444' }}
                                                                        onClick={() => handleDeletePiperVersion(vIdx)} title="Xóa">
                                                                        <i className="fas fa-trash" style={{ fontSize: 10 }} />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                            {/* CUDA row — always present */}
                                                            <tr style={{
                                                                backgroundColor: cudaPlugin
                                                                    ? (cudaPlugin.exist ? '#f0fdf4' : '#fef2f2')
                                                                    : '#fafafa',
                                                                borderLeft: ver.version === piperManifest.latestVersion ? '3px solid #10b981' : '3px solid transparent'
                                                            }}>
                                                                <td style={{ padding: '4px 14px 4px 28px', fontSize: 11 }}>
                                                                    <i className="fab fa-nvidia" style={{ color: '#76b900', fontSize: 12, marginRight: 4 }}
                                                                        title="CUDA" />
                                                                    <span style={{ fontWeight: 600, color: '#76b900' }}>CUDA</span>
                                                                </td>
                                                                <td style={{ fontFamily: 'monospace', fontSize: 10, color: '#64748b' }}>
                                                                    {cudaPlugin ? (
                                                                        <>
                                                                            {cudaPlugin.file}
                                                                            <span style={{ marginLeft: 6, color: '#94a3b8' }}>
                                                                                &rarr; {cudaPlugin.extractPath}
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontFamily: 'inherit' }}>
                                                                            Chưa đính kèm
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    {cudaPlugin ? (
                                                                        <span style={{ color: cudaPlugin.exist ? '#10b981' : '#ef4444', fontSize: 12 }}>
                                                                            <i className={`fas ${cudaPlugin.exist ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
                                                                        </span>
                                                                    ) : (
                                                                        <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
                                                                    )}
                                                                </td>
                                                                <td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{cudaPlugin?.updatedAt || '—'}</td>
                                                                <td className="drm-action-cell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                    {cudaPlugin ? (
                                                                        <>
                                                                            <Button color="link" size="sm" style={{ padding: '0 3px', color: '#76b900' }}
                                                                                onClick={() => showUploadCudaDialog(vIdx)} title="Thay file CUDA">
                                                                                <i className="fas fa-upload" style={{ fontSize: 10 }} />
                                                                            </Button>
                                                                            <Button color="link" size="sm" style={{ padding: '0 3px', color: '#ef4444' }}
                                                                                onClick={() => handleRemoveCuda(vIdx)} title="Gỡ CUDA">
                                                                                <i className="fas fa-times" style={{ fontSize: 10 }} />
                                                                            </Button>
                                                                        </>
                                                                    ) : (
                                                                        <Button color="success" size="sm" className="drm-primary-action"
                                                                            style={{ fontSize: 10, padding: '1px 8px', lineHeight: '18px' }}
                                                                            onClick={() => showUploadCudaDialog(vIdx)}>
                                                                            <i className="fas fa-upload" style={{ marginRight: 3, fontSize: 9 }} />
                                                                            Tải lên
                                                                        </Button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {/* Other plugins (non-cuda) */}
                                                            {(ver.plugins || []).filter(p => p.id !== 'cuda').map((plugin, pIdx) => {
                                                                const realIdx = (ver.plugins || []).findIndex(p => p === plugin);
                                                                return (
                                                                    <tr key={plugin.id} style={{
                                                                        backgroundColor: plugin.exist ? '#f5f3ff' : '#fef2f2',
                                                                        borderLeft: ver.version === piperManifest.latestVersion ? '3px solid #10b981' : '3px solid transparent'
                                                                    }}>
                                                                        <td style={{ padding: '4px 14px 4px 28px' }}>
                                                                            <i className="fas fa-plug" style={{ color: '#6366f1', fontSize: 10, marginRight: 4 }} />
                                                                            <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>{plugin.id}</span>
                                                                        </td>
                                                                        <td style={{ fontFamily: 'monospace', fontSize: 10, color: '#64748b' }}>
                                                                            {plugin.file}
                                                                            <span style={{ marginLeft: 6, color: '#a78bfa' }}>&rarr; {plugin.extractPath}</span>
                                                                        </td>
                                                                        <td style={{ textAlign: 'center' }}>
                                                                            <span style={{ color: plugin.exist ? '#10b981' : '#ef4444', fontSize: 12 }}>
                                                                                <i className={`fas ${plugin.exist ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
                                                                            </span>
                                                                        </td>
                                                                        <td style={{ fontSize: 10, color: '#94a3b8' }}>
                                                                            {plugin.required && <Badge color="warning" style={{ fontSize: 8 }}>Req</Badge>}
                                                                        </td>
                                                                        <td className="drm-action-cell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                            <Button color="link" size="sm" style={{ padding: '0 3px', color: '#3b82f6' }}
                                                                                onClick={() => handleEditPlugin(vIdx, realIdx)} title="Sửa plugin">
                                                                                <i className="fas fa-pen" style={{ fontSize: 9 }} />
                                                                            </Button>
                                                                            <Button color="link" size="sm" style={{ padding: '0 3px', color: '#ef4444' }}
                                                                                onClick={() => handleDeletePluginFromVersion(vIdx, realIdx)} title="Xóa plugin">
                                                                                <i className="fas fa-times" style={{ fontSize: 10 }} />
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                {(!piperManifest?.versions || piperManifest.versions.length === 0) && (
                                                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 20, fontSize: 13 }}>
                                                        Chưa có phiên bản engine
                                                    </td></tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>

                                {/* ── Right: Voice Catalog ── */}
                                <Col md="6">
                                    <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <i className="fas fa-microphone-alt" style={{ color: '#f59e0b' }} />
                                            Voice Catalog
                                            <Badge color="light" style={{ fontSize: 10, color: '#64748b' }}>
                                                {(piperManifest?.catalog || []).reduce((sum, g) => sum + (g.voices || []).length, 0)} voices
                                            </Badge>
                                        </h6>
                                        <Button color="warning" size="sm" className="drm-primary-action" style={{ fontSize: 11 }} onClick={showAddPiperCatalogGroupDialog}>
                                            <i className="fas fa-plus" style={{ marginRight: 4 }} />Nguồn
                                        </Button>
                                    </div>
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                        {(piperManifest?.catalog || []).map((group, gIdx) => (
                                            <div key={gIdx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                {/* Group header */}
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    padding: '10px 16px', backgroundColor: '#fffbeb'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                        <i className="fas fa-box-open" style={{ color: '#d97706', fontSize: 13 }} />
                                                        <strong style={{ fontSize: 13, color: '#92400e' }}>{group.name}</strong>
                                                        <span style={{ fontSize: 10, color: '#b45309', fontFamily: 'monospace' }}>{group.id}</span>
                                                        <Badge color="light" style={{ fontSize: 9, color: '#92400e' }}>{group.language}</Badge>
                                                        {group.license && <Badge color="light" style={{ fontSize: 9, color: '#92400e' }}>{group.license}</Badge>}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#10b981', fontSize: 10 }}
                                                            onClick={() => showAddVoiceToGroupDialog(gIdx)} title="Thêm voice">
                                                            <i className="fas fa-plus" style={{ fontSize: 9 }} />
                                                        </Button>
                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#3b82f6' }}
                                                            onClick={() => handleEditCatalogGroup(gIdx)} title="Sửa nguồn">
                                                            <i className="fas fa-pen" style={{ fontSize: 9 }} />
                                                        </Button>
                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#ef4444' }}
                                                            onClick={() => handleDeleteCatalogGroup(gIdx)} title="Xóa nguồn">
                                                            <i className="fas fa-trash" style={{ fontSize: 9 }} />
                                                        </Button>
                                                    </div>
                                                </div>
                                                {/* Model Pack row */}
                                                {group.modelFile && (
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                        padding: '5px 16px 5px 32px', fontSize: 11,
                                                        backgroundColor: group.modelExist ? '#f0fdf4' : '#fef2f2',
                                                        borderTop: '1px solid #f1f5f9'
                                                    }}>
                                                        <i className="fas fa-brain" style={{ color: '#8b5cf6', fontSize: 10 }} />
                                                        <span style={{ fontWeight: 600, color: '#6d28d9' }}>Model Pack</span>
                                                        <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#94a3b8' }}>{group.modelFile}</span>
                                                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{formatSize(group.modelSize)}</span>
                                                        <span style={{ color: group.modelExist ? '#10b981' : '#ef4444' }}>
                                                            <i className={`fas ${group.modelExist ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
                                                        </span>
                                                        {group.hasPreprocessing && (
                                                            <Badge color="info" style={{ fontSize: 8, padding: '1px 5px' }}>Preprocessing</Badge>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Voice table */}
                                                <Table hover size="sm" style={{ margin: 0, fontSize: 12 }}>
                                                    <tbody>
                                                        {(group.voices || []).map((voice, vIdx) => (
                                                            <tr key={vIdx}>
                                                                <td style={{ padding: '5px 16px 5px 32px', width: '35%' }}>
                                                                    <i className="fas fa-microphone" style={{ color: '#a78bfa', fontSize: 10, marginRight: 6 }} />
                                                                    <span style={{ fontWeight: 600 }}>{voice.name}</span>
                                                                </td>
                                                                <td style={{ fontFamily: 'monospace', fontSize: 10, color: '#64748b' }}>{voice.file}</td>
                                                                <td style={{ fontSize: 10, color: '#94a3b8', width: 60 }}>{formatSize(voice.size)}</td>
                                                                <td style={{ textAlign: 'center', width: 30 }}>
                                                                    <span style={{ color: voice.exist ? '#10b981' : '#ef4444', fontSize: 12 }}>
                                                                        <i className={`fas ${voice.exist ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
                                                                    </span>
                                                                </td>
                                                                <td className="drm-action-cell" style={{ textAlign: 'center', width: 60, whiteSpace: 'nowrap' }}>
                                                                    <Button color="link" size="sm" style={{ padding: '0 2px', color: '#3b82f6' }}
                                                                        onClick={() => handleEditVoice(gIdx, vIdx)} title="Sửa">
                                                                        <i className="fas fa-pen" style={{ fontSize: 9 }} />
                                                                    </Button>
                                                                    <Button color="link" size="sm" style={{ padding: '0 2px', color: '#ef4444' }}
                                                                        onClick={() => handleDeleteVoiceFromGroup(gIdx, vIdx)} title="Xóa">
                                                                        <i className="fas fa-times" style={{ fontSize: 9 }} />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {(!group.voices || group.voices.length === 0) && (
                                                            <tr><td colSpan={5} style={{ padding: '10px 32px', color: '#cbd5e1', fontSize: 11, fontStyle: 'italic' }}>
                                                                Chưa có voice
                                                            </td></tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ))}
                                        {(!piperManifest?.catalog || piperManifest.catalog.length === 0) && (
                                            <div style={{ padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                                                <i className="fas fa-cubes" style={{ fontSize: 24, display: 'block', marginBottom: 8, opacity: 0.3 }} />
                                                Chưa có nguồn model nào
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </CardBody>
                </Card>

                {/* ═══════ OmniVoice Engine Card ═══════ */}
                <Card className="drm-card" style={{ borderRadius: 15, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', marginBottom: 20 }}>
                    <CardHeader className="drm-card-header" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', padding: '16px 24px', borderRadius: '15px 15px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h4 style={{ color: '#fff', margin: 0, fontWeight: 700 }}><i className="fas fa-headset" style={{ marginRight: 10 }} />OmniVoice Engine</h4>
                            <Badge color="light" style={{ fontSize: 13, color: '#7c3aed', fontWeight: 700, padding: '5px 10px' }}>{omniVoiceManifest?.latestVersion || '—'}</Badge>
                            {omniVoiceSaving && <Spinner size="sm" style={{ color: '#fff' }} />}
                        </div>
                    </CardHeader>
                    <CardBody style={{ padding: '20px 24px' }}>
                        {omniVoiceLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spinner size="sm" color="primary" /></div> : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                    <h6 style={{ fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <i className="fas fa-code-branch" style={{ color: '#7c3aed' }} />Phiên bản Engine
                                    </h6>
                                    <Button color="primary" size="sm" className="drm-primary-action" onClick={showAddOmniVoiceVersionDialog}><i className="fas fa-plus" style={{ marginRight: 5 }} /> Thêm</Button>
                                </div>
                                <div className="drm-engine-table">
                                    <Table hover responsive size="sm" style={{ margin: 0 }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}><tr><th style={{ padding: '8px 14px' }}>Version</th><th>File</th><th style={{ textAlign: 'center', width: 40 }}><i className="fas fa-check-circle" title="Tồn tại" /></th><th>Ngày</th><th style={{ width: 100, textAlign: 'center' }}></th></tr></thead>
                                        <tbody>
                                            {(omniVoiceManifest?.versions || []).map((ver, vIdx) => (
                                                <tr key={ver.version} style={{ borderLeft: ver.version === omniVoiceManifest.latestVersion ? '3px solid #7c3aed' : '3px solid transparent' }}>
                                                    <td style={{ padding: '6px 14px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
                                                        {ver.version}
                                                        {ver.version === omniVoiceManifest.latestVersion && <Badge color="success" style={{ marginLeft: 6, fontSize: 9 }}>Latest</Badge>}
                                                    </td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{ver.engineFile}</td>
                                                    <td style={{ textAlign: 'center' }}><span style={{ color: ver.exist ? '#10b981' : '#ef4444', fontSize: 14 }}><i className={`fas ${ver.exist ? 'fa-check-circle' : 'fa-times-circle'}`} /></span></td>
                                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{ver.releaseDate || '—'}</td>
                                                    <td className="drm-action-cell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#3b82f6' }} onClick={() => handleEditOmniVoiceVersion(vIdx)} title="Sửa"><i className="fas fa-pen" style={{ fontSize: 10 }} /></Button>
                                                        {ver.version !== omniVoiceManifest.latestVersion && <Button color="link" size="sm" style={{ padding: '0 3px', color: '#f59e0b' }} onClick={() => handleSetOmniVoiceLatest(ver.version)} title="Đặt Latest"><i className="fas fa-star" style={{ fontSize: 10 }} /></Button>}
                                                        <Button color="link" size="sm" style={{ padding: '0 3px', color: '#ef4444' }} onClick={() => handleDeleteOmniVoiceVersion(vIdx)} title="Xóa"><i className="fas fa-trash" style={{ fontSize: 10 }} /></Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!omniVoiceManifest?.versions || omniVoiceManifest.versions.length === 0) && (
                                                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 20, fontSize: 13 }}>Chưa có phiên bản</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Speech AI Models: OpenAI Speech + Coqui XTTS */}
                {(() => {
                    const speechAiCats = AI_CATEGORIES.filter(c => c.title === 'Speech & Image' || c.title === 'Speech (Coqui XTTS)');
                    return speechAiCats.length > 0 && (
                        <Card className="drm-card drm-card-ai" style={{ borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden', marginTop: 8 }}>
                            <CardHeader className="drm-card-header" style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', padding: '16px 24px', borderBottom: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <i className="fas fa-robot" style={{ fontSize: 22, color: '#fff', opacity: 0.9 }} />
                                    <Label style={{ fontSize: 16, color: '#fff', fontWeight: 700, marginBottom: 0 }}>Speech AI Models</Label>
                                </div>
                            </CardHeader>
                            <CardBody style={{ padding: '20px 24px' }}>
                                {aiLoading ? <div style={{ textAlign: 'center', padding: 20 }}><Spinner size="sm" color="primary" /></div> : speechAiCats.map((cat, catIdx) => {
                                    const filteredKeys = cat.title === 'Speech & Image' ? cat.keys.filter(k => k.key.includes('speech')) : cat.keys;
                                    if (filteredKeys.length === 0) return null;
                                    return (
                                        <div key={catIdx} style={{ marginBottom: catIdx < speechAiCats.length - 1 ? 24 : 0 }}>
                                            <div className="drm-ai-category-header" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', borderRadius: 10 }}>
                                                <i className={cat.icon} style={{ fontSize: 16 }} />
                                                <Label style={{ fontWeight: 700, fontSize: 14, marginBottom: 0 }}>{cat.title}</Label>
                                            </div>
                                            <div className="drm-provider-grid" style={{ columnCount: 2, columnGap: 16 }}>
                                                {filteredKeys.map(({ key, label, hasSize, isRepo }) => {
                                                    const isEditing = editingKey === key;
                                                    const raw = aiSettings[key];
                                                    const models = raw ? (typeof raw === 'string' ? raw.split(',').filter(Boolean) : raw) : [];
                                                    return (
                                                        <div key={key} className="provider-card">
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                                <Label style={{ fontWeight: 600, fontSize: 13, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    <i className="fas fa-circle" style={{ fontSize: 7, color: cat.color }} />{label}
                                                                    <Badge color="light" style={{ fontSize: 10, marginLeft: 4 }}>{models.length}</Badge>
                                                                </Label>
                                                                {isEditing ? (
                                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                                        <button className="btn-icon-sm" style={{ background: '#eef7ee', color: '#2f855a' }} onClick={saveAiEdit}><i className="fas fa-check" /></button>
                                                                        <button className="btn-icon-sm" style={{ background: '#f8f9fa', color: '#999' }} onClick={cancelEdit}><i className="fas fa-times" /></button>
                                                                    </div>
                                                                ) : (
                                                                    <button className="btn-icon-sm" style={{ background: '#f0f7ff', color: '#3498db' }} onClick={() => startEdit(key)}><i className="fas fa-pen" /></button>
                                                                )}
                                                            </div>
                                                            {isEditing ? (
                                                                <div>
                                                                    {(editModels || []).map((m, idx) => (
                                                                        <div key={idx} className="edit-row">
                                                                            <input value={m?.name || ''} onChange={e => updateAiModel(idx, 'name', e.target.value)} placeholder="Tên hiển thị" style={{ flex: 1 }} />
                                                                            <input value={isRepo ? (m?.repo || '') : (m?.model || '')} onChange={e => updateAiModel(idx, isRepo ? 'repo' : 'model', e.target.value)} placeholder={isRepo ? 'Repo URL' : 'Model ID'} style={{ flex: 1 }} />
                                                                            <button className="btn-icon-sm" style={{ background: '#fff0f0', color: '#e74c3c' }} onClick={() => removeAiModel(idx)}><i className="fas fa-times" /></button>
                                                                        </div>
                                                                    ))}
                                                                    <button className="btn-icon-sm" style={{ background: '#f0f7ff', color: '#3498db', width: '100%', borderRadius: 8, marginTop: 6 }} onClick={addAiModel}><i className="fas fa-plus" /></button>
                                                                </div>
                                                            ) : (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                                    {models.length > 0 ? models.map((m, i) => {
                                                                        const mName = typeof m === 'object' ? m.name : m;
                                                                        const mModel = typeof m === 'object' ? (m.model || m.repo || '') : '';
                                                                        return <span key={i} className="ai-model-badge">{mName}{mModel && <span className="model-id" style={{ marginLeft: 4 }}>{mModel}</span>}</span>;
                                                                    }) : <span style={{ color: '#999', fontSize: 12, fontStyle: 'italic' }}>Chưa có</span>}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </CardBody>
                        </Card>
                    );
                })()}
            </>)}

            {/* ═══════ TAB: Translate ═══════ */}
            {activeTab === 'translate' && (<>
                {(() => {
                    const translateCats = AI_CATEGORIES.filter(c => c.title === 'Translate' || c.title === 'Ollama (Local AI)');
                    return (
                        <Card className="drm-card drm-card-ai" style={{ borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
                            <CardHeader className="drm-card-header" style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', padding: '20px 24px', borderBottom: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <i className="fas fa-language" style={{ fontSize: 28, color: '#fff', opacity: 0.9 }} />
                                    <div style={{ lineHeight: 1.3 }}>
                                        <Label style={{ fontSize: 18, color: '#fff', fontWeight: 700, marginBottom: 0, display: 'block' }}>Translate AI Models</Label>
                                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Quản lý mô hình dịch thuật</span>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody className="drm-card-body" style={{ padding: '20px 24px' }}>
                                {aiLoading ? <div style={{ textAlign: 'center', padding: 20 }}><Spinner size="sm" color="primary" /></div> : translateCats.map((cat, catIdx) => (
                                    <div key={catIdx} style={{ marginBottom: catIdx < translateCats.length - 1 ? 24 : 0 }}>
                                        <div className="drm-ai-category-header" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', borderRadius: 10 }}>
                                            <i className={cat.icon} style={{ fontSize: 16 }} />
                                            <Label style={{ fontWeight: 700, fontSize: 14, marginBottom: 0 }}>{cat.title}</Label>
                                        </div>
                                        <div className="drm-provider-grid" style={{ columnCount: 2, columnGap: 16 }}>
                                            {cat.keys.map(({ key, label, hasSize, isRepo }) => {
                                                const isEditing = editingKey === key;
                                                const raw = aiSettings[key];
                                                const models = raw ? (typeof raw === 'string' ? raw.split(',').filter(Boolean) : raw) : [];
                                                return (
                                                    <div key={key} className="provider-card">
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                            <Label style={{ fontWeight: 600, fontSize: 13, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-circle" style={{ fontSize: 7, color: cat.color }} />{label}<Badge color="light" style={{ fontSize: 10, marginLeft: 4 }}>{models.length}</Badge></Label>
                                                            {isEditing ? (
                                                                <div style={{ display: 'flex', gap: 4 }}>
                                                                    <button className="btn-icon-sm" style={{ background: '#eef7ee', color: '#2f855a' }} onClick={saveAiEdit}><i className="fas fa-check" /></button>
                                                                    <button className="btn-icon-sm" style={{ background: '#f8f9fa', color: '#999' }} onClick={cancelEdit}><i className="fas fa-times" /></button>
                                                                </div>
                                                            ) : (
                                                                <button className="btn-icon-sm" style={{ background: '#f0f7ff', color: '#3498db' }} onClick={() => startEdit(key)}><i className="fas fa-pen" /></button>
                                                            )}
                                                        </div>
                                                        {isEditing ? (
                                                            <div>{(editModels || []).map((m, idx) => (<div key={idx} className="edit-row"><input value={m?.name || ''} onChange={e => updateAiModel(idx, 'name', e.target.value)} placeholder="Tên hiển thị" style={{ flex: 1 }} /><input value={isRepo ? (m?.repo || '') : (m?.model || '')} onChange={e => updateAiModel(idx, isRepo ? 'repo' : 'model', e.target.value)} placeholder={isRepo ? 'Repo URL' : 'Model ID'} style={{ flex: 1 }} /><button className="btn-icon-sm" style={{ background: '#fff0f0', color: '#e74c3c' }} onClick={() => removeAiModel(idx)}><i className="fas fa-times" /></button></div>))}<button className="btn-icon-sm" style={{ background: '#f0f7ff', color: '#3498db', width: '100%', borderRadius: 8, marginTop: 6 }} onClick={addAiModel}><i className="fas fa-plus" /></button></div>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>{models.length > 0 ? models.map((m, i) => { const mName = typeof m === 'object' ? m.name : m; const mModel = typeof m === 'object' ? (m.model || m.repo || '') : ''; return <span key={i} className="ai-model-badge">{mName}{mModel && <span className="model-id" style={{ marginLeft: 4 }}>{mModel}</span>}</span>; }) : <span style={{ color: '#999', fontSize: 12, fontStyle: 'italic' }}>Chưa có</span>}</div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    );
                })()}
            </>)}

            {/* ═══════ TAB: Content Generation ═══════ */}
            {activeTab === 'content' && (<>
                {(() => {
                    const contentCats = AI_CATEGORIES.filter(c => c.title === 'Content Generation');
                    const imageCats = AI_CATEGORIES.filter(c => c.title === 'Speech & Image').map(c => ({ ...c, title: 'Image & Whisper', keys: c.keys.filter(k => k.key.includes('image') || k.key.includes('whisper')) })).filter(c => c.keys.length > 0);
                    const allCats = [...contentCats, ...imageCats];
                    return (
                        <Card className="drm-card drm-card-ai" style={{ borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
                            <CardHeader className="drm-card-header" style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', padding: '20px 24px', borderBottom: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <i className="fas fa-pen-fancy" style={{ fontSize: 28, color: '#fff', opacity: 0.9 }} />
                                    <div style={{ lineHeight: 1.3 }}>
                                        <Label style={{ fontSize: 18, color: '#fff', fontWeight: 700, marginBottom: 0, display: 'block' }}>Content Generation AI Models</Label>
                                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Quản lý mô hình tạo nội dung, hình ảnh</span>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody className="drm-card-body" style={{ padding: '20px 24px' }}>
                                {aiLoading ? <div style={{ textAlign: 'center', padding: 20 }}><Spinner size="sm" color="primary" /></div> : allCats.map((cat, catIdx) => (
                                    <div key={catIdx} style={{ marginBottom: catIdx < allCats.length - 1 ? 24 : 0 }}>
                                        <div className="drm-ai-category-header" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', borderRadius: 10 }}>
                                            <i className={cat.icon || 'fas fa-image'} style={{ fontSize: 16 }} />
                                            <Label style={{ fontWeight: 700, fontSize: 14, marginBottom: 0 }}>{cat.title}</Label>
                                        </div>
                                        <div className="drm-provider-grid" style={{ columnCount: 2, columnGap: 16 }}>
                                            {cat.keys.map(({ key, label, hasSize, isRepo }) => {
                                                const isEditing = editingKey === key;
                                                const raw = aiSettings[key];
                                                const models = raw ? (typeof raw === 'string' ? raw.split(',').filter(Boolean) : raw) : [];
                                                return (
                                                    <div key={key} className="provider-card">
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                            <Label style={{ fontWeight: 600, fontSize: 13, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-circle" style={{ fontSize: 7, color: cat.color || '#e67e22' }} />{label}<Badge color="light" style={{ fontSize: 10, marginLeft: 4 }}>{models.length}</Badge></Label>
                                                            {isEditing ? (
                                                                <div style={{ display: 'flex', gap: 4 }}>
                                                                    <button className="btn-icon-sm" style={{ background: '#eef7ee', color: '#2f855a' }} onClick={saveAiEdit}><i className="fas fa-check" /></button>
                                                                    <button className="btn-icon-sm" style={{ background: '#f8f9fa', color: '#999' }} onClick={cancelEdit}><i className="fas fa-times" /></button>
                                                                </div>
                                                            ) : (
                                                                <button className="btn-icon-sm" style={{ background: '#f0f7ff', color: '#3498db' }} onClick={() => startEdit(key)}><i className="fas fa-pen" /></button>
                                                            )}
                                                        </div>
                                                        {isEditing ? (
                                                            <div>{(editModels || []).map((m, idx) => (<div key={idx} className="edit-row"><input value={m?.name || ''} onChange={e => updateAiModel(idx, 'name', e.target.value)} placeholder="Tên hiển thị" style={{ flex: 1 }} /><input value={isRepo ? (m?.repo || '') : (m?.model || '')} onChange={e => updateAiModel(idx, isRepo ? 'repo' : 'model', e.target.value)} placeholder={isRepo ? 'Repo URL' : 'Model ID'} style={{ flex: 1 }} /><button className="btn-icon-sm" style={{ background: '#fff0f0', color: '#e74c3c' }} onClick={() => removeAiModel(idx)}><i className="fas fa-times" /></button></div>))}<button className="btn-icon-sm" style={{ background: '#f0f7ff', color: '#3498db', width: '100%', borderRadius: 8, marginTop: 6 }} onClick={addAiModel}><i className="fas fa-plus" /></button></div>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>{models.length > 0 ? models.map((m, i) => { const mName = typeof m === 'object' ? m.name : m; const mModel = typeof m === 'object' ? (m.model || m.repo || '') : ''; return <span key={i} className="ai-model-badge">{mName}{mModel && <span className="model-id" style={{ marginLeft: 4 }}>{mModel}</span>}</span>; }) : <span style={{ color: '#999', fontSize: 12, fontStyle: 'italic' }}>Chưa có</span>}</div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    );
                })()}
            </>)}
        </div>
    );
}

export default DHBRenderModel;
